/* eslint-disable no-unused-vars, brace-style, camelcase, no-case-declarations, no-undef */
const VC_PHONE_RULES = {
  '+44': { min: 10, max: 10, mobilePrefixes: ['7'] },
  '+353': { min: 9, max: 9, mobilePrefixes: ['8'] },
  '+33': { min: 9, max: 9, mobilePrefixes: ['6', '7'] },
  '+49': { min: 10, max: 11, mobilePrefixes: ['15', '16', '17'] },
  '+31': { min: 9, max: 9, mobilePrefixes: ['6'] },
  '+39': { min: 9, max: 10, mobilePrefixes: ['3'] },
  '+34': { min: 9, max: 9, mobilePrefixes: ['6', '7'] },
  '+351': { min: 9, max: 9, mobilePrefixes: ['9'] },
  '+32': { min: 9, max: 9, mobilePrefixes: ['4'] },
  '+41': { min: 9, max: 9, mobilePrefixes: ['7'] },
  '+43': { min: 10, max: 13, mobilePrefixes: ['6'] },
}

const VC_DEFAULT_PHONE_RULE = { min: 6, max: 14 }

class ButtonStateManager {
  constructor (vehicleChecker) {
    this.vehicleChecker = vehicleChecker
    this.container = new ButtonContainer(vehicleChecker.resetContainer, vehicleChecker)
    this.resetButton = new ResetButton(vehicleChecker.resetButton)
    this.isUpdating = false
  }

  updateButtonStates () {
    if (this.isUpdating) return
    this.isUpdating = true
    const hasAnyValue = this.hasAnyInputValue()
    if (!hasAnyValue) {
      this.hideAllButtons()
    } else {
      this.showResetButton()
    }
    this.isUpdating = false
  }

  hasAnyInputValue () {
    return [
      this.vehicleChecker.makeInput,
      this.vehicleChecker.modelInput,
      this.vehicleChecker.seriesInput,
      this.vehicleChecker.generationInput,
    ].some(input => input && input.value.trim() !== '')
  }

  hideAllButtons () {
    this.container.hide()
    this.resetButton.hide()
  }

  showResetButton () {
    this.container.show()
    this.resetButton.show()
  }
}
class ButtonContainer {
  constructor (container, vehicleChecker) {
    this.container = container
    this.vehicleChecker = vehicleChecker
  }

  show () {
    if (!this.container || !this.vehicleChecker) return
    this.container.classList.remove('hidden')
    this.container.style.display = ''
    this.vehicleChecker.classList.add('has-reset-button')
  }

  hide () {
    if (!this.container || !this.vehicleChecker) return
    this.container.classList.add('hidden')
    this.vehicleChecker.classList.remove('has-reset-button')
  }
}
class ResetButton {
  constructor (button) {
    this.button = button
  }

  show () {
    if (!this.button) return
    this.button.classList.remove('hidden')
  }

  hide () {
    if (!this.button) return
    this.button.classList.add('hidden')
  }
}

class VehicleChecker extends HTMLElement {
  static isDev = false
  async connectedCallback () {
    await this.init()
  }

  async init () {
    this.apiBase = this.getAttribute('data-api-base') || 'https://tentbox.tools/api/web/vehicle-checker'
    this.dataApiBase = `${this.apiBase}/v2`
    this.userGuidelinesUrl = this.getAttribute('data-user-guidelines-url') || ''
    this.apiHeaders = { Accept: 'application/json' }
    this.emailModalEnabled = this.getAttribute('data-enable-email-modal') === 'true'
    this.smsStepEnabled = this.getAttribute('data-enable-sms-step') === 'true'
    this.klaviyoListId = this.getAttribute('data-klaviyo-list-id') || ''
    this.market = this.getAttribute('data-market') || 'en'
    this.signupSource = this.getAttribute('data-signup-source') || ''
    this.store = this.getAttribute('data-store') || ''
    this.localeCountry = (this.getAttribute('data-locale-country') || '').toUpperCase()
    this.trackingConsentCountries = (this.getAttribute('data-tracking-consent-countries') || '')
      .split(',')
      .map((code) => code.trim().toUpperCase())
      .filter(Boolean)
    this.trackingConsentActive = false
    this.vehicleLabels = window.VehicleLabelTranslator
      ? new window.VehicleLabelTranslator(this.dataset.labelTranslations || '')
      : { active: false, display: (value) => value }
    this.cleanupSharedVehicleInfo()
    this.cacheElements()
    this.setDefaultPlaceholders()
    this.setupState()
    this.translations = JSON.parse(this.dataset.wordingObject || '{}')
    this.buttonManager = new ButtonStateManager(this)
    this.initializeFuzzySearch()
    try {
      await this.fetchAllBrands()
      const urlParams = this.parseUrlParams()
      const hasValidUrlParams = urlParams.make && (urlParams.series || urlParams.model || urlParams.generation)
      if (hasValidUrlParams) {
        localStorage.removeItem('sharedVehicleInfo')
        this._skipEmailModal = true
        const { make, model: seriesLevelName, series: variantName, generation, lastInput } = urlParams
        await this.restoreFromUrlParams({
          make,
          series: seriesLevelName,
          model: variantName,
          generation,
          lastInput,
        })
        if (this.isSelectionComplete() && this.roofLoadLimit !== null) {
          await this.renderTentboxesResult(this.roofLoadLimit)
        }
      } else {
        this._skipEmailModal = true
        await this.restorePreviousSelection()
      }
      this._skipEmailModal = false
      this.bindEvents()
    } catch (error) {
      console.error('Error initializing VehicleChecker:', error)
      this.showMaintenanceMessage()
    }
  }

  cleanupSharedVehicleInfo () {
    const sharedInfo = localStorage.getItem('sharedVehicleInfo')
    if (sharedInfo) {
      try {
        const parsed = JSON.parse(sharedInfo)
        if (!parsed?.make || parsed.make === 'undefined') {
          localStorage.removeItem('sharedVehicleInfo')
        }
      } catch (err) {
        localStorage.removeItem('sharedVehicleInfo')
      }
    }
  }

  setDefaultPlaceholders () {
    this.DEFAULT_PLACEHOLDERS = {
      make: this.makeInput?.getAttribute('placeholder') || '',
      model: this.modelInput?.getAttribute('placeholder') || '',
      year: this.seriesInput?.getAttribute('placeholder') || '',
      generation: this.generationInput?.getAttribute('placeholder') || '',
    }

    // Store placeholders in data attributes for retrieval; placeholders stay
    // visible at rest in the vc-widget design.
    if (this.makeInput) {
      this.makeInput.setAttribute('data-placeholder', this.DEFAULT_PLACEHOLDERS.make)
    }
    if (this.modelInput) {
      this.modelInput.setAttribute('data-placeholder', this.DEFAULT_PLACEHOLDERS.model)
    }
    if (this.seriesInput) {
      this.seriesInput.setAttribute('data-placeholder', this.DEFAULT_PLACEHOLDERS.year)
    }
    if (this.generationInput) {
      this.generationInput.setAttribute('data-placeholder', this.DEFAULT_PLACEHOLDERS.generation)
    }
  }

  setupState () {
    this.brandsData = []
    this.seriesDataByBrand = {}
    this.modelsDataBySeries = {}
    this.generationsDataByModel = {}
    this.selectedBrand = null
    this.selectedBrandGid = null
    this.selectedSeries = null
    this.selectedSeriesGid = null
    this.selectedModel = null
    this.selectedModelGid = null
    this.selectedGeneration = null
    this.roofLoadLimit = null
    this.roofLoadMeta = null
    this.searchLogged = false
    this.maintenanceMode = false
    this.loading = false
    this.resultsRendered = false
    this.initializeHiddenSections()
  }

  initializeHiddenSections () {
    const hiddenSectionsStr = this.dataset.hiddenSections
    if (!hiddenSectionsStr) {
      this.hiddenSectionIds = []
      return
    }
    this.hiddenSectionIds = hiddenSectionsStr.split(',').map(id => id.trim()).filter(Boolean)
    this.hideAllSections()
  }

  hideAllSections () {
    if (!this.hiddenSectionIds?.length) return
    this.hiddenSectionIds.forEach(sectionId => {
      const section = document.getElementById(sectionId)
      if (section) {
        section.style.display = 'none'
      }
    })
  }

  showAllSections () {
    if (!this.hiddenSectionIds?.length) return
    this.hiddenSectionIds.forEach(sectionId => {
      const section = document.getElementById(sectionId)
      if (section) {
        section.style.display = ''
      }
    })
  }

  parseUrlParams () {
    const searchParams = new URLSearchParams(window.location.search)
    return {
      make: searchParams.get('make'),
      series: searchParams.get('series'),
      model: searchParams.get('model'),
      generation: searchParams.get('generation'),
      lastInput: searchParams.get('last_input'),
    }
  }

  updateUrlParams (param, value) {
    const url = new URL(window.location.href)
    if (value) {
      url.searchParams.set(param, value)
    } else {
      url.searchParams.delete(param)
    }
    window.history.replaceState({}, '', url)
  }

  setLastInput (inputType) {
    this.updateUrlParams('last_input', inputType)
  }

  clearUrlParams () {
    const url = new URL(window.location.href)
    url.search = ''
    window.history.replaceState({}, '', url)
  }

  displayLabel (value) {
    return this.vehicleLabels ? this.vehicleLabels.display(value) : value
  }

  normalizeString (str) {
    if (!str) return ''

    // First normalize diacritics to their base characters
    const withoutDiacritics = str
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')

    const lowerStr = withoutDiacritics.toLowerCase()
    const normalizedStr = lowerStr
      .replace(/\s+/g, '')
      .replace(/-/g, '')
      .replace(/[^a-z0-9]/g, '')

    return normalizedStr
  }

  async restoreFromUrlParams (params) {
    this.setInputsLoading(true)
    try {
      const { make, series: urlSeries, model: urlModel, generation: urlGeneration, lastInput } = params
      if (!make || !this.brandsData?.length) {
        if (!this.brandsData?.length) await this.fetchAllBrands()
        if (!this.brandsData?.length) {
          this.setInputsLoading(false)
          return
        }
      }
      const brand = this.findItemWithFlexibleMatching(this.brandsData, make, 'name')
      if (!brand) {
        this.clearUrlParams()
        return
      }
      this.selectedBrand = brand.name
      this.selectedBrandGid = brand.gid
      this.makeInput.value = this.displayLabel(brand.name)
      this.markInputAsCompleted(this.makeInput)
      this.modelInput.disabled = false
      if (urlSeries) {
        const seriesData = await this.fetchSeries(brand.gid)
        let seriesItem = this.findItemWithFlexibleMatching(seriesData, urlSeries, 'name')
        if (!seriesItem && urlSeries.includes('+')) {
          const urlSeriesWithSpaces = urlSeries.replace(/\+/g, ' ')
          seriesItem = this.findItemWithFlexibleMatching(seriesData, urlSeriesWithSpaces, 'name')
        }
        if (seriesItem) {
          this.selectedSeries = seriesItem.name
          this.selectedSeriesGid = seriesItem.gid
          this.modelInput.value = this.displayLabel(seriesItem.name)
          this.markInputAsCompleted(this.modelInput)
          this.seriesInput.disabled = false
          const checkSeriesResponse = await this.apiFetch(`/serie/${seriesItem.gid}/check-models`)
          let modelItem = null
          let isSingleModelScenario = false
          if (checkSeriesResponse?.success && checkSeriesResponse.result?.singleModel) {
            modelItem = checkSeriesResponse.result.singleModel
            isSingleModelScenario = true
            if (!urlModel || this.isStringMatch(urlModel, modelItem.name)) {
              this.selectedModel = modelItem.name
              this.selectedModelGid = modelItem.gid
              this.seriesInput.value = this.displayLabel(modelItem.name)
              this.markInputAsCompleted(this.seriesInput)
              this.generationInput.disabled = false
            } else {
              modelItem = null
            }
          } else if (urlModel) {
            const modelsData = await this.fetchModels(seriesItem.gid)
            modelItem = this.findItemWithFlexibleMatching(modelsData, urlModel, 'name')
            if (!modelItem && urlModel.includes('+')) {
              const urlModelWithSpaces = urlModel.replace(/\+/g, ' ')
              modelItem = this.findItemWithFlexibleMatching(modelsData, urlModelWithSpaces, 'name')
            }
            if (modelItem) {
              this.selectedModel = modelItem.name
              this.selectedModelGid = modelItem.gid
              this.seriesInput.value = this.displayLabel(modelItem.name)
              this.markInputAsCompleted(this.seriesInput)
              this.generationInput.disabled = false
            }
          }
          if (modelItem) {
            const checkModelResponse = await this.apiFetch(`/model/${modelItem.gid}/check-generations`)
            if (checkModelResponse?.success && checkModelResponse.result?.vehicleIncompatible === true) {
              this.resetInputState(this.generationInput, this.replaceShortcodes(this.translations?.all_generations || 'All Generations'))
              this.generationInput.disabled = true
              this.roofLoadLimit = 0
              this.roofLoadMeta = this.metaFromCheckResult(checkModelResponse.result)
              await this.renderTentboxesResult(this.roofLoadLimit)
            } else if (checkModelResponse?.success && checkModelResponse.result?.hasUniformRoofLoad) {
              this.resetInputState(this.generationInput, this.replaceShortcodes(this.translations?.all_generations || 'All Generations'))
              this.generationInput.disabled = true
              this.roofLoadLimit = checkModelResponse.result.uniformRoofLoadValue
              this.roofLoadMeta = this.metaFromCheckResult(checkModelResponse.result)
              await this.renderTentboxesResult(this.roofLoadLimit)
            } else if (urlGeneration) {
              const generationsData = await this.fetchGenerations(modelItem.gid)
              let generationMatch = generationsData.find(g => {
                const genLabel = `${g.start_year}-${g.end_year === 0 || !g.end_year ? 'present' : g.end_year}`
                return genLabel === urlGeneration || this.isStringMatch(genLabel, urlGeneration)
              })
              if (!generationMatch && urlGeneration.includes('+')) {
                const urlGenWithSpaces = urlGeneration.replace(/\+/g, ' ')
                generationMatch = generationsData.find(g => {
                  const genLabel = `${g.start_year}-${g.end_year === 0 || !g.end_year ? 'present' : g.end_year}`
                  return genLabel === urlGenWithSpaces || this.isStringMatch(genLabel, urlGenWithSpaces)
                })
              }
              if (generationMatch) {
                const genLabel = `${generationMatch.start_year}-${generationMatch.end_year === 0 || !generationMatch.end_year ? 'present' : generationMatch.end_year}`
                this.selectedGeneration = genLabel
                this.generationInput.value = this.displayLabel(genLabel)
                this.markInputAsCompleted(this.generationInput)
                this.roofLoadLimit = generationMatch.roofLoadLimit
                this.setRoofLoadMeta(generationMatch)
                await this.renderTentboxesResult(this.roofLoadLimit)
              } else {
                this.resetInputState(this.generationInput, this.DEFAULT_PLACEHOLDERS.generation, false)
              }
            } else {
              this.resetInputState(this.generationInput, this.DEFAULT_PLACEHOLDERS.generation, false)
            }
          } else if (!isSingleModelScenario) {
            this.resetInputState(this.seriesInput, this.DEFAULT_PLACEHOLDERS.year, false)
          }
        } else {
          this.resetInputState(this.modelInput, this.DEFAULT_PLACEHOLDERS.model, false)
        }
      }
      this.buttonManager.updateButtonStates()
      this.storeCurrentSelection()
      if (this.isSelectionComplete() && this.roofLoadLimit !== null) {
        await this.renderTentboxesResult(this.roofLoadLimit)
      }
      this.setInputsLoading(false)
      setTimeout(() => {
        this.hideAllCompletedSuggestions()
        if (this.isSelectionComplete()) {
          this.blurActiveElement()
        } else {
          const nextInputType = this.determineNextInput(lastInput)
          this.focusInputElement(nextInputType)
        }
      }, 300)
    } catch (error) {
      console.error('Error restoring from URL params:', error)
      this.setInputsLoading(false)
      this.clearUrlParams()
      this.resetForm()
    }
  }

  hideAllCompletedSuggestions () {
    if (!this.makeSuggestions || !this.modelSuggestions || !this.seriesSuggestions || !this.generationSuggestions) return
    if (this.selectedBrand) this.hideSuggestions(this.makeSuggestions)
    if (this.selectedSeries) this.hideSuggestions(this.modelSuggestions)
    if (this.selectedModel) this.hideSuggestions(this.seriesSuggestions)
    if (this.selectedGeneration || this.generationInput?.disabled) this.hideSuggestions(this.generationSuggestions)
  }

  isSelectionComplete () {
    return !!this.selectedGeneration || (!!this.selectedModel && this.generationInput?.disabled)
  }

  blurActiveElement () {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur()
    }
  }

  focusInputElement (inputType) {
    let inputToFocus = null
    switch (inputType) {
      case 'make':
        inputToFocus = this.makeInput
        break
      case 'series':
        inputToFocus = this.modelInput
        break
      case 'model':
        inputToFocus = this.seriesInput
        break
      case 'generation':
        inputToFocus = this.generationInput
        break
    }
    if (inputToFocus && !inputToFocus.disabled) {
      inputToFocus.focus()
    } else {
      this.blurActiveElement()
    }
  }

  determineNextInput (lastInput) {
    if (this.isSelectionComplete()) {
      return null
    }
    if (this.makeInput && !this.makeInput.disabled && !this.selectedBrand) return 'make'
    if (this.modelInput && !this.modelInput.disabled && !this.selectedSeries) return 'series'
    if (this.seriesInput && !this.seriesInput.disabled && !this.selectedModel) return 'model'
    if (this.generationInput && !this.generationInput.disabled && !this.selectedGeneration) return 'generation'
    if (lastInput) {
      if (lastInput === 'brand' && !this.selectedSeries && !this.modelInput?.disabled) return 'series'
      if (lastInput === 'series' && !this.selectedModel && !this.seriesInput?.disabled) return 'model'
      if (lastInput === 'model' && !this.selectedGeneration && !this.generationInput?.disabled) return 'generation'
      if (lastInput === 'generation' && !this.selectedGeneration && !this.generationInput?.disabled) return 'generation'
    }
    return null
  }

  initializeFuzzySearch () {
    const fuseScriptUrl = 'https://cdn.jsdelivr.net/npm/fuse.js@7.0.0/dist/fuse.min.js'
    const initFuse = () => {
      if (typeof Fuse !== 'undefined') {
        this.fuseOptions = {
          threshold: 0.5,
          ignoreLocation: true,
          includeScore: true,
          minMatchCharLength: 1,
          findAllMatches: true,
          keys: ['label'],
        }
      } else {
        console.error('Fuse.js library not found.')
      }
    }
    if (typeof Fuse === 'undefined' && !document.querySelector(`script[src="${fuseScriptUrl}"]`)) {
      const script = document.createElement('script')
      script.src = fuseScriptUrl
      script.onload = initFuse
      script.onerror = () => console.error('Failed to load Fuse.js script.')
      document.head.appendChild(script)
    } else if (typeof Fuse !== 'undefined') {
      initFuse()
    }
  }

  cacheElements () {
    const root = this.querySelector('.vc-widget')
    if (!root) {
      console.error('Vehicle selection tool root element not found.')
      this.showMaintenanceMessage('Tool setup failed: Root element missing.')
      return
    }
    this.makeInput = root.querySelector('#make-input')
    this.modelInput = root.querySelector('#model-input')
    this.seriesInput = root.querySelector('#series-input')
    this.generationInput = root.querySelector('#generation-input')

    // Cache floating labels
    this.makeLabel = root.querySelector('label[for="make-input"]')
    this.modelLabel = root.querySelector('label[for="model-input"]')
    this.seriesLabel = root.querySelector('label[for="series-input"]')
    this.generationLabel = root.querySelector('label[for="generation-input"]')

    this.resetContainer = root.querySelector('.vc-widget__reset')
    this.resetButton = root.querySelector('.vc-widget__reset-link')
    this.makeSuggestions = root.querySelector('#make-suggestions')
    this.modelSuggestions = root.querySelector('#model-suggestions')
    this.seriesSuggestions = root.querySelector('#series-suggestions')
    this.generationSuggestions = root.querySelector('#generation-suggestions')
    this.resultsEl = root.querySelector('#vehicle-results')
    this.tentboxesResultsEl = root.querySelector('#tentboxes-results')
    this.tentboxesResultsContainer = this.tentboxesResultsEl?.querySelector('.tentboxes-results__container')
    this.tentboxesResultsTitle = this.tentboxesResultsEl?.querySelector('.tentboxes-results__title')

    // Store the initial title content for reset functionality
    if (this.tentboxesResultsTitle) {
      this.initialTitleContent = this.tentboxesResultsTitle.innerHTML
    }

    if (this.tentboxesResultsEl && !this.tentboxesResultsContainer) {
      this.tentboxesResultsContainer = document.createElement('div')
      this.tentboxesResultsContainer.classList.add('tentboxes-results__container')
      this.tentboxesResultsEl.appendChild(this.tentboxesResultsContainer)
    }
    this.makeResetBtn = this.makeInput?.parentElement?.querySelector('.vc-widget__field-clear')
    this.modelResetBtn = this.modelInput?.parentElement?.querySelector('.vc-widget__field-clear')
    this.seriesResetBtn = this.seriesInput?.parentElement?.querySelector('.vc-widget__field-clear')
    this.generationResetBtn = this.generationInput?.parentElement?.querySelector('.vc-widget__field-clear')
    this.contactCTA = root.querySelector('.contact-cta')
    this.resultContainer = this.querySelector('[data-result-container]')
    this.showResultsContainer = root.querySelector('.vc-widget__show-results')
    this.showResultsButton = root.querySelector('.vc-widget__show-results-button')
    this.chipsList = this.querySelector('[data-compatibility-chips]')
    this.noticeMount = this.querySelector('[data-notice-mount]')
    this.checkAnotherContainer = this.querySelector('[data-check-another]')
    const checkAnotherLink = this.checkAnotherContainer?.querySelector('.vc-widget__check-another-link')
    if (checkAnotherLink && !checkAnotherLink._vcBound) {
      checkAnotherLink._vcBound = true
      checkAnotherLink.addEventListener('click', () => this.resetForm())
    }
  }

  hideElement (element) {
    if (element) element.classList.add('hidden')
  }

  showElement (element, displayType = 'block') {
    if (element) {
      element.classList.remove('hidden')
    }
  }

  bindEvents () {
    if (!this.makeInput || !this.makeSuggestions || !this.makeResetBtn ||
        !this.modelInput || !this.modelSuggestions || !this.modelResetBtn ||
        !this.seriesInput || !this.seriesSuggestions || !this.seriesResetBtn ||
        !this.generationInput || !this.generationSuggestions || !this.generationResetBtn) {
      console.error('One or more input/suggestion/reset elements missing, cannot bind events.')
      this.showMaintenanceMessage('Tool setup failed: Input elements missing.')
      return
    }
    this.bindSuggestionInput(
      this.makeInput, this.makeSuggestions,
      this.showBrandSuggestions.bind(this),
      (label, gid) => this.selectBrand(label, gid),
      () => true, this.makeResetBtn, () => this.resetForm(),
    )
    this.bindSuggestionInput(
      this.modelInput, this.modelSuggestions,
      this.showSeriesSuggestions.bind(this),
      (label, gid) => this.selectSeries(label, gid),
      () => !!this.selectedBrandGid, this.modelResetBtn, () => this.resetFromSeries(),
    )
    this.bindSuggestionInput(
      this.seriesInput, this.seriesSuggestions,
      this.showModelSuggestions.bind(this),
      (label, gid) => this.selectModel(label, gid),
      () => !!this.selectedSeriesGid, this.seriesResetBtn, () => this.resetFromModel(),
    )
    this.bindSuggestionInput(
      this.generationInput, this.generationSuggestions,
      this.showGenerationSuggestions.bind(this),
      (label, gid, roofLoad) => this.selectGeneration(label, gid, roofLoad),
      () => !!this.selectedModelGid, this.generationResetBtn, () => this.resetFromGeneration(),
    )
    if (this.resetButton) {
      this.resetButton.addEventListener('click', () => this.resetForm())
    }
    if (this.showResultsButton) {
      this.showResultsButton.addEventListener('click', () => this.handleShowResultsClick())
    }
  }

  updateInputAccessoryVisibility (input, resetButton) {
    if (!input) return
    const chevron = input.parentElement?.querySelector('.input-chevron')
    if (chevron) chevron.classList.toggle('hidden', input.disabled)
    if (resetButton) this.hideElement(resetButton)
  }

  observeInputDisabledState (input, callback) {
    if (!input) return null
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'disabled') {
          callback()
        }
      })
    })
    observer.observe(input, { attributes: true })
    return observer
  }

  bindSuggestionInput (input, suggestionsContainer, showCallback, selectCallback, enableCondition = () => true, resetButton, resetCallback) {
    if (!input || !suggestionsContainer || !resetButton || !resetCallback) return
    const isNonSearchable = input === this.seriesInput || input === this.generationInput
    const handleInput = () => {
      if (enableCondition()) {
        input.classList.remove('completed')
        showCallback(input.value)
        this.updateInputAccessoryVisibility(input, resetButton)
        this.buttonManager.updateButtonStates()
      }
    }
    const handleFocus = () => {
      if (enableCondition()) {
        // Show placeholder when focused
        const storedPlaceholder = input.getAttribute('data-placeholder') || input.getAttribute('placeholder')
        if (storedPlaceholder && !input.disabled) {
          input.placeholder = storedPlaceholder
        }

        if (isNonSearchable) {
          showCallback('')
        } else {
          showCallback(input.value)
        }
        this.updateInputAccessoryVisibility(input, resetButton)
      }
    }
    const handleBlur = (e) => {
      setTimeout(() => {
        const focusedElement = document.activeElement
        const isSuggestionFocused = suggestionsContainer.contains(focusedElement)
        const isResetFocused = resetButton.contains(focusedElement)
        if (!isSuggestionFocused && !isResetFocused) {
          this.hideSuggestions(suggestionsContainer)
          if (!input.value && input.classList.contains('completed')) {
            let restoredValue = null
            if (input === this.makeInput) restoredValue = this.selectedBrand
            else if (input === this.modelInput) restoredValue = this.selectedSeries
            else if (input === this.seriesInput) restoredValue = this.selectedModel
            else if (input === this.generationInput) restoredValue = this.selectedGeneration
            if (restoredValue) input.value = this.displayLabel(restoredValue)
          }
          if (!input.value) {
            input.classList.remove('completed')
          }
          this.updateInputAccessoryVisibility(input, resetButton)
        }
      }, 150)
    }
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        const firstItem = suggestionsContainer.querySelector('li[data-value]')
        firstItem?.focus()
      } else if (e.key === 'Escape') {
        e.preventDefault()
        this.hideSuggestions(suggestionsContainer)
        input.focus()
      }
    }
    const handleSuggestionMouseDown = (e) => {
      e.preventDefault()
      const li = e.target.closest('li[data-value]')
      if (li) {
        selectCallback(li.dataset.value, li.dataset.gid, li.dataset.roofload)
      }
    }
    const handleSuggestionKeyDown = (e) => {
      this.handleSuggestionsKeydown(e, input, suggestionsContainer, (li) => {
        selectCallback(li.dataset.value, li.dataset.gid, li.dataset.roofload)
      })
    }
    input.addEventListener('input', handleInput)
    input.addEventListener('focus', handleFocus)
    input.addEventListener('blur', handleBlur)
    input.addEventListener('keydown', handleKeyDown)
    suggestionsContainer.addEventListener('mousedown', handleSuggestionMouseDown)
    suggestionsContainer.addEventListener('keydown', handleSuggestionKeyDown)
    resetButton.addEventListener('click', (e) => {
      e.preventDefault()
      resetCallback()
      setTimeout(() => input.focus(), 0)
      this.updateInputAccessoryVisibility(input, resetButton)
    })
    this.updateInputAccessoryVisibility(input, resetButton)
    this.observeInputDisabledState(input, () => this.updateInputAccessoryVisibility(input, resetButton))
  }

  async apiFetch (endpoint, options = {}) {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 8000)
    try {
      const headers = {
        ...this.apiHeaders,
        ...(options.method === 'POST' ? { 'Content-Type': 'application/json' } : {}),
        ...options.headers,
      }
      const base = endpoint.startsWith('/log-result') ? this.apiBase : this.dataApiBase
      const response = await fetch(`${base}${endpoint}`, {
        headers,
        signal: controller.signal,
        ...options,
      })
      clearTimeout(timeoutId)
      if (!response.ok) {
        let errorDetails = ''
        try { errorDetails = await response.text() } catch { /* ignore */ }
        console.error(`API Error ${response.status}: ${response.statusText} on ${endpoint}`, errorDetails)
        throw new Error(`Request failed: ${response.status}`)
      }
      const contentType = response.headers.get('content-type')
      if (contentType?.includes('application/json')) {
        return await response.json()
      } else {
        return await response.text()
      }
    } catch (error) {
      clearTimeout(timeoutId)
      if (error.name !== 'AbortError') {
        console.error('API Fetch Error:', error.message, 'on', endpoint)
        if (!this.maintenanceMode) {
          this.showMaintenanceMessage()
        }
      }
      throw error
    }
  }

  async fetchAllBrands () {
    if (this.brandsData?.length > 0) return
    try {
      const data = await this.apiFetch('/brands')
      if (data?.success && Array.isArray(data.result)) {
        this.brandsData = data.result
      } else {
        console.error('Invalid brand data received:', data)
        throw new Error('Invalid data received for brands')
      }
    } catch (err) {
      console.error('Failed to fetch brands:', err)
      if (!this.maintenanceMode) throw err
    }
  }

  async fetchSeries (brandGid) {
    if (!brandGid) return []
    if (this.seriesDataByBrand[brandGid]) return this.seriesDataByBrand[brandGid]
    try {
      const data = await this.apiFetch(`/serie/${brandGid}`)
      if (data?.success && Array.isArray(data.result)) {
        this.seriesDataByBrand[brandGid] = data.result
        return data.result
      }
      console.warn('No series data or invalid format for brand:', brandGid, data)
      this.seriesDataByBrand[brandGid] = []
      return []
    } catch (err) {
      console.error('Failed to fetch series for brand:', brandGid, err)
      this.seriesDataByBrand[brandGid] = []
      return []
    }
  }

  async fetchModels (seriesGid) {
    if (!seriesGid) return []
    if (this.modelsDataBySeries[seriesGid]) return this.modelsDataBySeries[seriesGid]
    try {
      const data = await this.apiFetch(`/model/${seriesGid}`)
      if (data?.success && Array.isArray(data.result)) {
        this.modelsDataBySeries[seriesGid] = data.result
        return data.result
      }
      console.warn('No model data or invalid format for series:', seriesGid, data)
      this.modelsDataBySeries[seriesGid] = []
      return []
    } catch (err) {
      console.error('Failed to fetch models for series:', seriesGid, err)
      this.modelsDataBySeries[seriesGid] = []
      return []
    }
  }

  async fetchGenerations (modelGid) {
    if (!modelGid) return []
    if (this.generationsDataByModel[modelGid]) return this.generationsDataByModel[modelGid]
    try {
      const data = await this.apiFetch(`/gid/s/${modelGid}`)
      if (data?.success && Array.isArray(data?.result)) {
        const generations = data.result
          .map(gen => {
            const incompatible = gen.incompatible === true
            const explicitValue = gen.effectiveRoofLoadLimit ?? gen.roofLoadLimit
            const roofLoadAttr = gen.attributes?.find(a => a.name === 'Max Roof Load')
            const roofLoadValue = explicitValue ?? roofLoadAttr?.values?.[0]
            const numericRoofLoad = Number(roofLoadValue)
            if (!incompatible && (roofLoadValue === null || roofLoadValue === undefined || isNaN(numericRoofLoad))) return null
            const manufacturerValue = gen.manufacturerRoofLoadLimit
            return {
              gid: gen.gid,
              name: gen.name,
              start_year: gen.start_year,
              end_year: gen.end_year,
              roofLoadLimit: isNaN(numericRoofLoad) ? 0 : numericRoofLoad,
              manufacturerRoofLoadLimit: manufacturerValue === null || manufacturerValue === undefined ? null : Number(manufacturerValue),
              isOverridden: gen.isOverridden === true,
              incompatible,
            }
          })
          .filter(gen => gen !== null)
        this.generationsDataByModel[modelGid] = generations
        return generations
      }
      console.warn('No generation data or invalid format for model:', modelGid, data)
      this.generationsDataByModel[modelGid] = []
      return []
    } catch (err) {
      console.error('Failed to fetch generations for model:', modelGid, err)
      this.generationsDataByModel[modelGid] = []
      return []
    }
  }

  setRoofLoadMeta (source) {
    if (!source) {
      this.roofLoadMeta = null
      return
    }
    const manufacturer = source.manufacturerRoofLoadLimit
    this.roofLoadMeta = {
      manufacturer: manufacturer === null || manufacturer === undefined || isNaN(Number(manufacturer)) ? null : Number(manufacturer),
      isOverridden: source.isOverridden === true,
      incompatible: source.incompatible === true,
    }
  }

  metaFromCheckResult (result) {
    if (!result) return null
    const gens = Array.isArray(result.generations) ? result.generations : []
    if (result.vehicleIncompatible === true || (gens.length > 0 && gens.every(g => g.incompatible === true))) {
      return { manufacturer: null, isOverridden: false, incompatible: true }
    }
    const compatible = gens.filter(g => g.incompatible !== true)
    const manufacturers = [...new Set(
      compatible
        .map(g => g.manufacturerRoofLoadLimit)
        .filter(v => v !== null && v !== undefined && !isNaN(Number(v)))
        .map(Number),
    )]
    const allOverridden = compatible.length > 0 && compatible.every(g => g.isOverridden === true)
    return {
      manufacturer: manufacturers.length === 1 ? manufacturers[0] : null,
      isOverridden: allOverridden && manufacturers.length === 1,
      incompatible: false,
    }
  }

  getScenario (parsedRoofLoad) {
    const meta = this.roofLoadMeta
    if ((meta && meta.incompatible) || parsedRoofLoad <= 0) return 'incompatible'
    if (meta && meta.isOverridden && meta.manufacturer !== null && meta.manufacturer !== parsedRoofLoad) return 'overridden'
    return 'standard'
  }

  async fetchProperties (gid) {
    if (!gid) return null
    try {
      return await this.apiFetch(`/gid/s/${gid}`)
    } catch (err) {
      console.error('Failed to fetch properties for GID:', gid, err)
      return null
    }
  }

  async showBrandSuggestions (query) {
    if (!this.brandsData?.length) await this.fetchAllBrands()
    if (!this.brandsData?.length) {
      this.renderSuggestions([], this.makeSuggestions); return
    }
    const items = this.brandsData.map(f => ({ label: f.name, display: this.displayLabel(f.name), gid: f.gid }))
    const filteredItems = query ? this.filterItemsWithFuse(items, query) : items
    if (query) {
      this.renderSuggestions(filteredItems, this.makeSuggestions)
      return
    }
    this.renderBrandSuggestionsWithCategories(filteredItems, this.makeSuggestions)
  }

  renderBrandSuggestionsWithCategories (items, container) {
    if (!container) return
    this.removeInputLoading(container)
    this.clearSuggestions(container)
    if (!items || items.length === 0) {
      const li = document.createElement('li')
      li.textContent = this.replaceShortcodes(this.translations?.no_results_found || 'No results found')
      li.classList.add('px-3.5', 'py-2.5', 'text-gray-500', 'cursor-default')
      container.appendChild(li)
      return
    }
    const sortedItems = [...items].sort((a, b) => (a.display || a.label).localeCompare(b.display || b.label))
    const popularBrands = this.brandsData.slice(0, 15)
      .map(brand => ({ label: brand.name, display: this.displayLabel(brand.name), gid: brand.gid }))
      .sort((a, b) => (a.display || a.label).localeCompare(b.display || b.label))
    const popularHeading = document.createElement('li')
    popularHeading.textContent = this.translations?.popular_makes || 'Popular'
    popularHeading.classList.add(
      'px-3.5', 'py-2', 'font-semibold', 'text-smol',
      'cursor-default', 'bg-white',
    )
    container.appendChild(popularHeading)
    popularBrands.forEach(item => {
      const li = document.createElement('li')
      li.textContent = item.display || item.label
      li.dataset.value = item.label
      li.dataset.gid = item.gid
      li.setAttribute('tabindex', '0')
      li.setAttribute('role', 'option')
      li.classList.add(
        'pl-4.5', 'pr-3.5', 'py-2.5', 'cursor-pointer', 'text-sm',
        'hover:bg-oat', 'focus:bg-oat', 'focus:outline',
        'focus:outline-2', 'focus:outline-oat', 'focus:outline-offset-[-1px]',
      )
      container.appendChild(li)
    })
    const divider = document.createElement('li')
    divider.classList.add('border-t', 'border-steel', 'my-1', 'cursor-default')
    divider.setAttribute('aria-hidden', 'true')
    container.appendChild(divider)
    const allMakesHeading = document.createElement('li')
    allMakesHeading.textContent = this.translations?.all_makes || 'All makes'
    allMakesHeading.classList.add(
      'px-3.5', 'py-2', 'font-semibold', 'text-smol',
      'cursor-default', 'bg-white',
    )
    container.appendChild(allMakesHeading)
    sortedItems.forEach(item => {
      const li = document.createElement('li')
      li.textContent = item.display || item.label
      li.dataset.value = item.label
      li.dataset.gid = item.gid
      li.setAttribute('tabindex', '0')
      li.setAttribute('role', 'option')
      li.classList.add(
        'pl-4.5', 'pr-3.5', 'py-2.5', 'cursor-pointer', 'text-sm',
        'hover:bg-oat', 'focus:bg-oat', 'focus:outline',
        'focus:outline-2', 'focus:outline-oat', 'focus:outline-offset-[-1px]',
      )
      container.appendChild(li)
    })
    this.showSuggestions(container)
  }

  async showSeriesSuggestions (query) {
    if (!this.selectedBrandGid) return
    this.showInputLoading(this.modelSuggestions)
    const series = await this.fetchSeries(this.selectedBrandGid)
    const items = series.map(f => ({ label: f.name, display: this.displayLabel(f.name), gid: f.gid }))
    const filteredItems = this.filterItemsWithFuse(items, query)
    await this.renderSuggestionsWithDelay(filteredItems, this.modelSuggestions)
  }

  async showModelSuggestions (query) {
    if (!this.selectedSeriesGid) return
    this.showInputLoading(this.seriesSuggestions)
    const models = await this.fetchModels(this.selectedSeriesGid)
    const items = models.map(f => ({ label: f.name, display: this.displayLabel(f.name), gid: f.gid }))
    const filteredItems = this.filterItemsWithFuse(items, query)
    await this.renderSuggestionsWithDelay(filteredItems, this.seriesSuggestions)
  }

  async showGenerationSuggestions (query) {
    if (!this.selectedModelGid) return
    this.showInputLoading(this.generationSuggestions)
    try {
      const generationsData = await this.fetchGenerations(this.selectedModelGid)
      const generations = generationsData
        .map(gen => {
          const label = `${gen.start_year}-${gen.end_year === 0 || !gen.end_year ? 'present' : gen.end_year}`
          return { label, display: this.displayLabel(label), gid: gen.gid, roofload: gen.roofLoadLimit }
        })
        .sort((a, b) => parseInt(b.label.split('-')[0], 10) - parseInt(a.label.split('-')[0], 10))
      await this.renderSuggestionsWithDelay(generations, this.generationSuggestions)
    } catch (error) {
      console.error('Error showing generation suggestions:', error)
      this.renderSuggestions([], this.generationSuggestions)
    }
  }

  filterItemsWithFuse (items, query) {
    if (!query?.trim()) return items
    if (typeof Fuse === 'undefined' || !this.fuseOptions) {
      console.warn('Fuse.js not available, using basic filter.')
      const lQuery = query.toLowerCase()
      return items.filter(item => item.label.toLowerCase().includes(lQuery) ||
        (item.display || '').toLowerCase().includes(lQuery))
    }

    const normalizedQuery = this.normalizeString(query)
    const queryLower = query.toLowerCase()

    // First, find direct prefix matches for prioritization
    const directMatches = []
    const otherItems = []

    items.forEach(item => {
      const normalizedLabel = this.normalizeString(item.label)
      const labelLower = item.label.toLowerCase()
      const displayLabel = item.display || item.label
      const normalizedDisplay = this.normalizeString(displayLabel)
      const displayLower = displayLabel.toLowerCase()

      // Check for exact prefix matches
      if (normalizedLabel.startsWith(normalizedQuery) || labelLower.startsWith(queryLower) ||
          normalizedDisplay.startsWith(normalizedQuery) || displayLower.startsWith(queryLower)) {
        directMatches.push({
          ...item,
          searchQuery: query,
          isDirectMatch: true,
        })
      } else {
        otherItems.push(item)
      }
    })

    // If we have direct matches and the query is short, prioritize them
    if (directMatches.length > 0 && query.length <= 3) {
      // Sort direct matches by length (shorter names first for better relevance)
      directMatches.sort((a, b) => a.label.length - b.label.length)

      // For short queries, if we have good direct matches, return them first
      if (query.length <= 2) {
        const fuzzyResults = this.fuzzySearchItems(otherItems, query).slice(0, 10)
        return directMatches.concat(fuzzyResults)
      }
    }

    // For longer queries or when we need more results, do full fuzzy search
    return this.fuzzySearchItems(items, query)
  }

  fuzzySearchItems (items, query) {
    const normalizedQuery = this.normalizeString(query)
    const queryLower = query.toLowerCase()

    // Create enhanced items with normalized versions and prefix scoring
    const enhancedItems = items.map(item => {
      const normalizedLabel = this.normalizeString(item.label)
      const labelLower = item.label.toLowerCase()
      const displayLabel = item.display || item.label
      const normalizedDisplay = this.normalizeString(displayLabel)
      const displayLower = displayLabel.toLowerCase()

      // Calculate prefix match bonuses
      let prefixBonus = 0
      if (normalizedLabel.startsWith(normalizedQuery) || normalizedDisplay.startsWith(normalizedQuery)) {
        prefixBonus = 0.9 // Strong bonus for normalized prefix match
      } else if (labelLower.startsWith(queryLower) || displayLower.startsWith(queryLower)) {
        prefixBonus = 0.8 // Good bonus for direct prefix match
      } else if (normalizedLabel.includes(normalizedQuery) || normalizedDisplay.includes(normalizedQuery)) {
        prefixBonus = 0.3 // Small bonus for containing the term
      }

      return {
        ...item,
        normalizedLabel,
        normalizedDisplay,
        prefixBonus,
        // Add searchable content that prioritizes prefix matches
        searchContent: prefixBonus > 0.5 ? `${normalizedQuery}${normalizedLabel}` : normalizedLabel,
        searchContentDisplay: prefixBonus > 0.5 ? `${normalizedQuery}${normalizedDisplay}` : normalizedDisplay,
      }
    })

    // Use more lenient threshold for very short queries
    const isShortQuery = query.length <= 2
    const searchOptions = {
      ...this.fuseOptions,
      threshold: isShortQuery ? 0.8 : 0.6,
      keys: [
        { name: 'label', weight: 0.4 },
        { name: 'normalizedLabel', weight: 0.4 },
        { name: 'searchContent', weight: 0.2 },
      ],
    }

    const fuse = new Fuse(enhancedItems, searchOptions)

    // Search with both original and normalized query
    const originalResults = fuse.search(query)
    const normalizedResults = normalizedQuery !== query ? fuse.search(normalizedQuery) : []

    // Combine and enhance results with custom scoring
    const combinedScores = new Map()
    let allResults = originalResults.concat(normalizedResults)

    if (this.vehicleLabels?.active) {
      const translatedItems = enhancedItems
        .filter(item => (item.display || item.label) !== item.label)
        .map(item => ({
          ...item,
          label: item.display,
          normalizedLabel: item.normalizedDisplay,
          searchContent: item.searchContentDisplay,
        }))
      if (translatedItems.length) {
        // Tighter threshold: this pass only exists to catch queries typed in the
        // storefront language, not to add a second round of fuzzy near-misses
        const displayFuse = new Fuse(translatedItems, {
          ...searchOptions,
          threshold: isShortQuery ? 0.4 : 0.3,
        })
        allResults = allResults.concat(displayFuse.search(query))
        if (normalizedQuery !== query) {
          allResults = allResults.concat(displayFuse.search(normalizedQuery))
        }
      }
    }

    allResults.forEach(result => {
      const item = result.item
      const existingResult = combinedScores.get(item.gid)

      // Apply prefix bonus to the score (lower score is better in Fuse.js)
      const adjustedScore = result.score * (1 - item.prefixBonus)

      if (!existingResult || adjustedScore < existingResult.adjustedScore) {
        combinedScores.set(item.gid, {
          ...result,
          adjustedScore,
          searchQuery: query,
        })
      }
    })

    // Convert back to array and sort by adjusted score
    const canonicalItems = new Map(items.map(item => [item.gid, item]))
    const sortedResults = Array.from(combinedScores.values())
      .sort((a, b) => a.adjustedScore - b.adjustedScore)
      .map(result => ({
        ...result.item,
        ...(canonicalItems.get(result.item.gid) || {}),
        searchQuery: query,
      }))

    return sortedResults
  }

  async renderSuggestionsWithDelay (items, container) {
    if (!container) return
    const startTime = Date.now()
    const minimumDelay = 250
    this.renderSuggestions(items, container)
    const elapsedTime = Date.now() - startTime
    const remainingDelay = minimumDelay - elapsedTime
    if (remainingDelay > 0) {
      await new Promise(resolve => setTimeout(resolve, remainingDelay))
    }
    this.removeInputLoading(container)
  }

  renderSuggestions (items, container) {
    if (!container) return
    this.removeInputLoading(container)
    this.clearSuggestions(container)
    if (!items || items.length === 0) {
      const li = document.createElement('li')
      li.textContent = this.replaceShortcodes(this.translations?.no_results_found || 'No results found')
      li.classList.add('px-3.5', 'py-2.5', 'text-gray-500', 'cursor-default')
      container.appendChild(li)
    } else {
      items.forEach((item) => {
        const li = document.createElement('li')
        li.innerHTML = this.highlightMatchingText(item.display || item.label, item.searchQuery || '')
        li.dataset.value = item.label
        li.dataset.gid = item.gid
        if (item.roofload !== undefined) li.dataset.roofload = item.roofload
        li.setAttribute('tabindex', '0')
        li.setAttribute('role', 'option')
        li.classList.add(
          'px-3.5', 'py-2.5', 'cursor-pointer', 'text-sm',
          'hover:bg-oat', 'focus:bg-oat', 'focus:outline',
          'focus:outline-2', 'focus:outline-oat', 'focus:outline-offset-[-1px]',
        )
        container.appendChild(li)
      })
    }
    this.showSuggestions(container)
  }

  clearSuggestions (container) {
    if (container) container.innerHTML = ''
  }

  showSuggestions (container) {
    if (container) {
      container.classList.remove('hidden')
      container.setAttribute('role', 'listbox')
      const inputId = container.id.replace('-suggestions', '-input')
      const input = document.getElementById(inputId)
      if (input) input.setAttribute('aria-expanded', 'true')
    }
  }

  hideSuggestions (container) {
    if (container) {
      container.classList.add('hidden')
      const inputId = container.id.replace('-suggestions', '-input')
      const input = document.getElementById(inputId)
      if (input) input.setAttribute('aria-expanded', 'false')
    }
  }

  showInputLoading (container) {
    if (!container) return
    this.clearSuggestions(container)
    const li = document.createElement('li')
    li.classList.add('px-3.5', 'py-2.5', 'flex', 'items-center', 'justify-center', 'input-loading-indicator')
    li.setAttribute('aria-live', 'polite')
    li.innerHTML = `
      <div class="h-5 w-5 text-black animate-spin" role="status">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
          <path d="M21 12a9 9 0 1 1-6.219-8.56"></path>
        </svg>
        <span class="sr-only">Loading...</span>
      </div>`
    container.appendChild(li)
    this.showSuggestions(container)
  }

  removeInputLoading (container) {
    if (!container) return
    const loadingIndicator = container.querySelector('.input-loading-indicator')
    loadingIndicator?.remove()
  }

  markInputAsCompleted (input) {
    if (!input) return
    input.classList.add('completed')
    const tickIcon = input.parentElement?.querySelector('.vc-widget__field-tick')
    if (tickIcon) tickIcon.classList.remove('hidden')
    const resetButton = input.parentElement?.querySelector('.vc-widget__field-clear')
    if (resetButton) {
      this.updateInputAccessoryVisibility(input, resetButton)
    }
  }

  selectBrand (brandName, brandGid, skipFocus = false) {
    this.selectedBrand = brandName
    this.selectedBrandGid = brandGid
    this.makeInput.value = this.displayLabel(brandName)
    this.markInputAsCompleted(this.makeInput)
    this.resetInputState(this.modelInput, this.DEFAULT_PLACEHOLDERS.model, false)
    this.resetInputState(this.seriesInput, this.DEFAULT_PLACEHOLDERS.year)
    this.resetInputState(this.generationInput, this.DEFAULT_PLACEHOLDERS.generation)
    this.selectedSeries = null; this.selectedSeriesGid = null
    this.selectedModel = null; this.selectedModelGid = null
    this.selectedGeneration = null; this.roofLoadLimit = null; this.roofLoadMeta = null
    this.resultsRendered = false // Reset the resultsRendered flag when starting a new vehicle check
    this.updateUrlParams('make', brandName)
    this.updateUrlParams('model', null)
    this.updateUrlParams('series', null)
    this.updateUrlParams('generation', null)
    this.setLastInput('brand')
    this.updateSharedVehicleStorage(brandName, null)
    this.storeCurrentSelection()
    this.hideSuggestions(this.makeSuggestions)
    this.clearResults()
    this.buttonManager.updateButtonStates()
    if (!skipFocus) {
      setTimeout(() => {
        this.focusInputElement('series')
        this.showSeriesSuggestions('')
      }, 100)
    }
  }

  resetForm () {
    this.buttonManager.hideAllButtons()
    this.blurActiveElement()
    this.clearUrlParams()
    this.selectedBrand = null; this.selectedBrandGid = null
    this.selectedSeries = null; this.selectedSeriesGid = null
    this.selectedModel = null; this.selectedModelGid = null
    this.selectedGeneration = null; this.roofLoadLimit = null; this.roofLoadMeta = null
    this.searchLogged = false
    this.resultsRendered = false
    this.resetInputState(this.makeInput, this.DEFAULT_PLACEHOLDERS.make, false)
    this.resetInputState(this.modelInput, this.DEFAULT_PLACEHOLDERS.model)
    this.resetInputState(this.seriesInput, this.DEFAULT_PLACEHOLDERS.year)
    this.resetInputState(this.generationInput, this.DEFAULT_PLACEHOLDERS.generation)
    localStorage.removeItem('vehicleSelection')
    localStorage.removeItem('vehicleCompatibility')
    localStorage.removeItem('sharedVehicleInfo')
    this.clearResults()
    this.showElement(this.contactCTA)
    // Keep results container visible in initial state - removed hideElement(this.tentboxesResultsEl)
    this.toggleChatWidget(true)
    const filtersRow = this.querySelector('.vc-widget__filters')
    if (filtersRow) this.scrollToElement(filtersRow, -100)
    setTimeout(() => this.focusInputElement('make'), 300)
  }

  resetInputState (input, placeholder, disabled = true) {
    if (!input) return
    input.value = ''
    input.classList.remove('completed')
    input.disabled = disabled

    input.setAttribute('placeholder', placeholder || '')

    const tickIcon = input.parentElement?.querySelector('.vc-widget__field-tick')
    if (tickIcon) tickIcon.classList.add('hidden')
    const resetButton = input.parentElement?.querySelector('.vc-widget__field-clear')
    if (resetButton) this.updateInputAccessoryVisibility(input, resetButton)
    const suggestionsId = input.id.replace('-input', '-suggestions')
    const suggestionsContainer = document.getElementById(suggestionsId)
    if (suggestionsContainer) this.hideSuggestions(suggestionsContainer)
  }

  async selectSeries (seriesName, seriesGid, skipFocus = false) {
    this.selectedSeries = seriesName
    this.selectedSeriesGid = seriesGid
    this.modelInput.value = this.displayLabel(seriesName)
    this.markInputAsCompleted(this.modelInput)
    this.resetInputState(this.seriesInput, this.DEFAULT_PLACEHOLDERS.year, false)
    this.resetInputState(this.generationInput, this.DEFAULT_PLACEHOLDERS.generation)
    this.selectedModel = null; this.selectedModelGid = null
    this.selectedGeneration = null; this.roofLoadLimit = null; this.roofLoadMeta = null
    this.updateUrlParams('model', seriesName)
    this.updateUrlParams('series', null)
    this.updateUrlParams('generation', null)
    this.setLastInput('series')
    this.updateSharedVehicleStorage(this.selectedBrand, seriesName)
    this.storeCurrentSelection()
    this.hideSuggestions(this.modelSuggestions)
    this.clearResults()
    try {
      const checkResponse = await this.apiFetch(`/serie/${seriesGid}/check-models`)
      if (checkResponse?.success && checkResponse.result?.singleModel) {
        const model = checkResponse.result.singleModel
        await this.selectModel(model.name, model.gid, skipFocus)
      } else {
        this.seriesInput.disabled = false
        if (!skipFocus) {
          setTimeout(() => {
            this.focusInputElement('model')
            this.showModelSuggestions('')
          }, 100)
        }
      }
    } catch (error) {
      console.error('Error checking models for series:', seriesGid, error)
      this.seriesInput.disabled = false
      if (!skipFocus) {
        setTimeout(() => {
          this.focusInputElement('model')
          this.showModelSuggestions('')
        }, 100)
      }
    } finally {
      this.buttonManager.updateButtonStates()
    }
  }

  async selectModel (modelName, modelGid, skipFocus = false) {
    this.selectedModel = modelName
    this.selectedModelGid = modelGid
    this.seriesInput.value = this.displayLabel(modelName)
    this.markInputAsCompleted(this.seriesInput)
    this.resetInputState(this.generationInput, this.DEFAULT_PLACEHOLDERS.generation, false)
    this.selectedGeneration = null; this.roofLoadLimit = null; this.roofLoadMeta = null
    this.updateUrlParams('series', modelName)
    this.updateUrlParams('generation', null)
    this.setLastInput('model')
    this.storeCurrentSelection()
    this.hideSuggestions(this.seriesSuggestions)
    this.clearResults()
    try {
      const checkResponse = await this.apiFetch(`/model/${modelGid}/check-generations`)
      if ((!checkResponse?.result?.generations || checkResponse?.result?.generations.length === 0) &&
           checkResponse?.success) {
        const directGenerations = await this.fetchGenerations(modelGid)
        if (directGenerations.length === 1) {
          this.resetInputState(this.generationInput, this.replaceShortcodes(this.translations?.all_generations || 'All Generations'))
          this.generationInput.disabled = true
          this.roofLoadLimit = directGenerations[0].roofLoadLimit
          this.setRoofLoadMeta(directGenerations[0])
          this.storeCompatibility(this.roofLoadLimit)
          this.storeCurrentSelection()
          this.buttonManager.updateButtonStates()
          if (!skipFocus) this.blurActiveElement()
          this.setLastInput('generation')
          await this.renderTentboxesResult(this.roofLoadLimit)
          return
        }
      }
      if (checkResponse?.success && checkResponse.result?.vehicleIncompatible === true) {
        this.resetInputState(this.generationInput, this.replaceShortcodes(this.translations?.all_generations || 'All Generations'))
        this.generationInput.disabled = true
        this.roofLoadLimit = 0
        this.roofLoadMeta = this.metaFromCheckResult(checkResponse.result)
        this.storeCompatibility(this.roofLoadLimit)
        this.storeCurrentSelection()
        this.buttonManager.updateButtonStates()
        if (!skipFocus) this.blurActiveElement()
        this.setLastInput('generation')
        await this.renderTentboxesResult(this.roofLoadLimit)
      } else if (checkResponse?.success && checkResponse.result?.hasUniformRoofLoad) {
        this.resetInputState(this.generationInput, this.replaceShortcodes(this.translations?.all_generations || 'All Generations'))
        this.generationInput.disabled = true
        this.roofLoadLimit = checkResponse.result.uniformRoofLoadValue
        this.roofLoadMeta = this.metaFromCheckResult(checkResponse.result)
        this.storeCompatibility(this.roofLoadLimit)
        this.storeCurrentSelection()
        this.buttonManager.updateButtonStates()
        if (!skipFocus) this.blurActiveElement()
        this.setLastInput('generation')
        await this.renderTentboxesResult(this.roofLoadLimit)
      } else {
        this.generationInput.disabled = false
        if (!skipFocus) {
          setTimeout(() => {
            this.focusInputElement('generation')
            this.showGenerationSuggestions('')
          }, 100)
        }
        this.buttonManager.updateButtonStates()
      }
    } catch (error) {
      console.error('Error checking generations for model:', modelGid, error)
      this.generationInput.disabled = false
      if (!skipFocus) {
        setTimeout(() => {
          this.focusInputElement('generation')
          this.showGenerationSuggestions('')
        }, 100)
      }
      this.buttonManager.updateButtonStates()
    }
  }

  async selectGeneration (generationLabel, generationGid, roofLoadValue, skipFocus = false) {
    let finalRoofLoad = roofLoadValue
    if (finalRoofLoad === null || finalRoofLoad === undefined) {
      console.warn('Roof load missing for generation, attempting fetch:', generationGid)
      try {
        const props = await this.fetchProperties(generationGid)
        const roofLoadAttribute = props?.result?.[0]?.attributes?.find(
          (prop) => prop.name === 'Max Roof Load',
        )
        finalRoofLoad = roofLoadAttribute?.values?.[0] || null
        if (finalRoofLoad === null) throw new Error('Roof load still missing after fetch.')
      } catch (error) {
        console.error('Failed to get roof load for generation:', generationGid, error)
        this.tentboxesResultsContainer.innerHTML = '<p class="py-24 text-center text-red-500">Failed to load vehicle details. Roof load missing.</p>'
        this.buttonManager.updateButtonStates()
        return
      }
    }
    this.selectedGeneration = generationLabel
    this.roofLoadLimit = finalRoofLoad
    const generationData = this.generationsDataByModel[this.selectedModelGid]?.find(
      gen => String(gen.gid) === String(generationGid),
    )
    this.setRoofLoadMeta(generationData)
    this.generationInput.value = this.displayLabel(generationLabel)
    this.markInputAsCompleted(this.generationInput)
    this.updateUrlParams('generation', generationLabel)
    this.setLastInput('generation')
    this.storeCompatibility(this.roofLoadLimit)
    this.storeCurrentSelection()
    this.hideSuggestions(this.generationSuggestions)
    this.clearResults()
    this.buttonManager.updateButtonStates()
    if (!skipFocus) this.blurActiveElement()
    await this.renderTentboxesResult(this.roofLoadLimit)
  }

  resetFromSeries () {
    this.resetInputState(this.modelInput, this.DEFAULT_PLACEHOLDERS.model, false)
    this.resetInputState(this.seriesInput, this.DEFAULT_PLACEHOLDERS.year)
    this.resetInputState(this.generationInput, this.DEFAULT_PLACEHOLDERS.generation)
    this.selectedSeries = null; this.selectedSeriesGid = null
    this.selectedModel = null; this.selectedModelGid = null
    this.selectedGeneration = null; this.roofLoadLimit = null; this.roofLoadMeta = null
    this.updateUrlParams('model', null)
    this.updateUrlParams('series', null)
    this.updateUrlParams('generation', null)
    this.setLastInput('brand')
    this.updateSharedVehicleStorage(this.selectedBrand, null)
    this.storeCurrentSelection()
    this.clearResults()
    this.buttonManager.updateButtonStates()
    this.toggleChatWidget(true)
    this.showSeriesSuggestions('')
  }

  resetFromModel () {
    this.resetInputState(this.seriesInput, this.DEFAULT_PLACEHOLDERS.year, false)
    this.resetInputState(this.generationInput, this.DEFAULT_PLACEHOLDERS.generation)
    this.selectedModel = null; this.selectedModelGid = null
    this.selectedGeneration = null; this.roofLoadLimit = null; this.roofLoadMeta = null
    this.updateUrlParams('series', null)
    this.updateUrlParams('generation', null)
    this.setLastInput('series')
    this.storeCurrentSelection()
    this.clearResults()
    this.buttonManager.updateButtonStates()
    this.toggleChatWidget(true)
    this.showModelSuggestions('')
  }

  resetFromGeneration () {
    this.resetInputState(this.generationInput, this.DEFAULT_PLACEHOLDERS.generation, false)
    this.selectedGeneration = null; this.roofLoadLimit = null; this.roofLoadMeta = null
    this.updateUrlParams('generation', null)
    this.setLastInput('model')
    this.storeCurrentSelection()
    this.clearResults()
    this.buttonManager.updateButtonStates()
    this.toggleChatWidget(true)
    this.showGenerationSuggestions('')
  }

  storeCurrentSelection () {
    const selection = {
      brand: this.selectedBrand ? { name: this.selectedBrand, gid: this.selectedBrandGid } : null,
      series: this.selectedSeries ? { name: this.selectedSeries, gid: this.selectedSeriesGid } : null,
      model: this.selectedModel ? { name: this.selectedModel, gid: this.selectedModelGid } : null,
      generation: (this.selectedGeneration || (this.generationInput?.disabled && this.roofLoadLimit))
        ? { label: this.selectedGeneration || this.replaceShortcodes(this.translations?.all_generations || 'All Generations'), roofLoadLimit: this.roofLoadLimit }
        : null,
    }
    if (selection.brand) {
      try {
        localStorage.setItem('vehicleSelection', JSON.stringify(selection))
      } catch (err) {
        console.error('Error storing vehicle selection in localStorage:', err)
      }
    } else {
      localStorage.removeItem('vehicleSelection')
    }
  }

  updateSharedVehicleStorage (make, model) {
    const sharedInfo = {}
    if (make) sharedInfo.make = make
    if (model) sharedInfo.model = model
    if (Object.keys(sharedInfo).length > 0) {
      try {
        localStorage.setItem('sharedVehicleInfo', JSON.stringify(sharedInfo))
      } catch (err) {
        console.error('Error storing shared vehicle info in localStorage:', err)
      }
    } else {
      localStorage.removeItem('sharedVehicleInfo')
    }
  }

  storeCompatibility (roofLoadLimit) {
    if (roofLoadLimit === null || roofLoadLimit === undefined) {
      localStorage.removeItem('vehicleCompatibility')
      return
    }
    try {
      const tentboxesData = JSON.parse(this.dataset.tentboxes || '{"tentboxes":[]}')
      const numericRoofLoad = Number(roofLoadLimit)
      const compatibleProducts = tentboxesData.tentboxes
        .filter(tentbox => Number(tentbox.weight) <= numericRoofLoad)
        .map(tentbox => tentbox.handle)
      localStorage.setItem('vehicleCompatibility', JSON.stringify({
        roofLoadLimit: numericRoofLoad,
        compatibleProducts,
        timestamp: Date.now(),
      }))
    } catch (err) {
      console.error('Error storing vehicle compatibility in localStorage:', err)
      localStorage.removeItem('vehicleCompatibility')
    }
  }

  storeBannerData () {
    if (!this.selectedBrand || !this.selectedSeries) {
      return
    }

    try {
      const bannerData = {
        make: this.selectedBrand,
        model: this.selectedSeries,
        timestamp: Date.now(),
      }
      localStorage.setItem('vehicleCheckerBannerData', JSON.stringify(bannerData))
    } catch (err) {
      console.error('Error storing vehicle checker banner data in localStorage:', err)
    }
  }

  scrollToElement (element, offset = 0) {
    if (!element) return
    const elementPosition = element.getBoundingClientRect().top + window.scrollY
    const offsetPosition = elementPosition + offset
    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth',
    })
  }

  scrollToTitle () {
    setTimeout(() => {
      const titleContainer = this.tentboxesResultsEl?.querySelector('.tentboxes-results__container')
      if (titleContainer) {
        this.scrollToElement(titleContainer, -100)
      }
    }, 100)
  }

  scrollToResults () {
    this.blurActiveElement()
    setTimeout(() => {
      const resultContainer = this.querySelector('.result-container')
      if (resultContainer) {
        const headerHeight = this.calculateHeaderHeight()
        this.scrollToElement(resultContainer, -headerHeight)
      }
    }, 100)
  }

  async renderTentboxesResult (roofLoadLimit) {
    const selectionKey = [
      this.selectedBrand,
      this.selectedSeries,
      this.selectedModel,
      this.selectedGeneration,
      roofLoadLimit,
    ].join('|')
    if (this.resultsRendered && this._lastRenderedSelectionKey === selectionKey) return
    const requiredElements = [this.tentboxesResultsContainer, this.tentboxesResultsEl]
    if (requiredElements.some(el => !el)) {
      console.error('Cannot render results: Required elements missing.')
      return
    }
    this.loading = true
    this.roofLoadLimit = roofLoadLimit
    this.hideElement(this.contactCTA)
    this.storeBannerData()

    const parsedRoofLoad = Number(roofLoadLimit)
    if (isNaN(parsedRoofLoad)) {
      console.error('Invalid roof load limit for rendering results:', roofLoadLimit)
      if (this.tentboxesResultsTitle) {
        this.tentboxesResultsTitle.textContent = 'Error: Invalid vehicle roof load data.'
      }
      this.loading = false
      return
    }

    const scenario = this.getScenario(parsedRoofLoad)
    const effectiveRoofLoad = scenario === 'incompatible' ? 0 : parsedRoofLoad

    try {
      await new Promise(resolve => setTimeout(resolve, 500))

      // Check if tentboxes data is valid JSON
      let tentboxesData
      try {
        tentboxesData = JSON.parse(this.dataset.tentboxes || '{"tentboxes":[]}')
      } catch (jsonError) {
        console.error('JSON Parse Error:', jsonError)
        console.error('Invalid JSON data:', this.dataset.tentboxes)
        throw new Error('Invalid tentboxes data configuration')
      }

      const allTentboxes = tentboxesData.tentboxes || []

      if (allTentboxes.length === 0) {
        console.error('No tentboxes configured in section settings')
        throw new Error('No tentboxes configured - please check section settings')
      }

      // Ensure all tentbox weights are valid numbers - exclude products with invalid weights
      allTentboxes.forEach(tentbox => {
        const weight = Number(tentbox.weight)
        if (isNaN(weight) || weight <= 0) {
          console.error(`❌ Invalid or missing weight for ${tentbox.handle}: "${tentbox.weight}" - this product will be excluded from compatibility check`)
          tentbox.weight = null // Mark as invalid so it gets filtered out
        }
      })

      // Filter out products with invalid weights
      const validTentboxes = allTentboxes.filter(tentbox => tentbox.weight !== null)

      if (validTentboxes.length === 0) {
        console.error('No valid tentboxes with weight data found')
        throw new Error('No valid tentboxes with weight data - please check product metafields')
      }

      const compatibleTentboxes = validTentboxes.filter(tentbox => {
        const weight = Number(tentbox.weight)
        return !isNaN(weight) && weight <= effectiveRoofLoad
      })
      const compatibleCount = compatibleTentboxes.length

      this.updateResultsTitle(compatibleCount, validTentboxes.length, scenario)

      this.updateCompatibilityChips(validTentboxes, effectiveRoofLoad, scenario)

      this.updateCardCompatibility(validTentboxes, effectiveRoofLoad)

      // Update container gap and padding for compatibility tags
      if (this.tentboxesResultsContainer) {
        this.tentboxesResultsContainer.classList.remove('gap-5')
        this.tentboxesResultsContainer.classList.add('gap-18', 'pt-10')
      }

      this.updateNoticeBox(parsedRoofLoad, scenario)

      if (this.checkAnotherContainer) {
        this.checkAnotherContainer.classList.toggle('hidden', scenario !== 'incompatible')
      }

      await this.logSearch()
      this.trackAnalyticsEvent(
        'vehicle-checker_Results',
        'results',
        this.buildCompatibilityAnalyticsData(
          validTentboxes,
          compatibleTentboxes,
          effectiveRoofLoad,
        ),
      )
      this._lastRenderedSelectionKey = selectionKey
      this.resultsRendered = true
      this.tentboxesResultsEl.classList.add('showing-results')

      if (this._skipEmailModal || !this.emailModalEnabled) {
        this.showResultsDirectly()
      } else {
        this.openEmailModal()
      }
    } catch (error) {
      console.error('Error rendering TentBox results:', error)
      if (this.tentboxesResultsTitle) {
        this.tentboxesResultsTitle.textContent = 'Failed to load compatible products. Please try again.'
      }
    } finally {
      setTimeout(() => {
        this.loading = false
      }, 500)
    }
  }

  // This function is no longer needed since cards are pre-rendered in Liquid
  // Keeping it commented for reference in case we need to revert
  /*
  createTentboxesSection (allTentboxes, parsedRoofLoad, compatibleCount) {
    // Function body removed - cards are now pre-rendered in Liquid
    // Title updates handled by updateResultsTitle()
    // Card compatibility handled by updateCardCompatibility()
    // Notice box handled by updateNoticeBox()
  }
  */

  updateResultsTitle (compatibleCount, totalCount, scenario) {
    if (!this.tentboxesResultsTitle) return

    let titleText
    if (scenario === 'incompatible') {
      titleText = this.translations?.incompatible_title || '<p>Your vehicle is not compatible with a TentBox.</p>'
    } else if (compatibleCount === 0) {
      titleText = this.translations?.no_compatible_models || '<p>No compatible TentBox models found</p>'
    } else if (compatibleCount === totalCount) {
      titleText = this.translations?.compatible_tentboxes_all || '<p>Your vehicle is compatible with all TentBox models:</p>'
    } else {
      const translationKey = compatibleCount === 1 ? 'compatible_tentboxes' : 'compatible_tentboxes_plural'
      const defaultText = compatibleCount === 1 ? '<p>Your vehicle is compatible with 1 TentBox model:</p>' : '<p>Your vehicle is compatible with [[compatibility_count]] TentBox models:</p>'
      titleText = this.translations?.[translationKey] || defaultText
    }

    this.tentboxesResultsTitle.innerHTML = this.replaceShortcodes(titleText, { compatibility_count: compatibleCount })
  }

  updateCompatibilityChips (tentboxes, roofLoadLimit, scenario) {
    if (!this.chipsList) return
    if (scenario === 'incompatible') {
      this.chipsList.classList.add('hidden')
      return
    }
    this.chipsList.classList.remove('hidden')
    const tentboxWeights = {}
    tentboxes.forEach(tentbox => {
      tentboxWeights[tentbox.handle] = Number(tentbox.weight)
    })
    this.chipsList.querySelectorAll('.vc-widget__chip').forEach(chip => {
      const handle = chip.dataset.handle
      const compatibleIcon = chip.querySelector('.vc-widget__chip-icon--compatible')
      const incompatibleIcon = chip.querySelector('.vc-widget__chip-icon--incompatible')
      if (!handle || !(handle in tentboxWeights)) {
        chip.classList.add('hidden')
        return
      }
      chip.classList.remove('hidden')
      const isCompatible = tentboxWeights[handle] <= roofLoadLimit
      if (compatibleIcon) compatibleIcon.classList.toggle('hidden', !isCompatible)
      if (incompatibleIcon) incompatibleIcon.classList.toggle('hidden', isCompatible)
    })
  }

  resetCompatibilityChips () {
    if (!this.chipsList) return
    this.chipsList.classList.remove('hidden')
    this.chipsList.querySelectorAll('.vc-widget__chip').forEach(chip => {
      chip.classList.remove('hidden')
      chip.querySelectorAll('.vc-widget__chip-icon').forEach(icon => icon.classList.add('hidden'))
    })
  }

  updateCardCompatibility (tentboxes, roofLoadLimit) {
    // Store last data for potential resize re-render
    this._lastTentboxesData = tentboxes
    this._lastRoofLoadLimit = roofLoadLimit

    const existingCards = this.tentboxesResultsContainer?.querySelectorAll('.tentbox-card')
    if (!existingCards?.length) return

    // Create a quick lookup of tentbox weights by handle
    const tentboxWeights = {}
    tentboxes.forEach(tentbox => {
      tentboxWeights[tentbox.handle] = Number(tentbox.weight)
    })

    existingCards.forEach((card, index) => {
      // Identify card by product URL
      const productLink = card.querySelector('a[href*="/products/"]')
      if (!productLink) {
        return
      }

      const productUrl = productLink.getAttribute('href')
      const handle = productUrl.split('/products/')[1]?.split('?')[0]

      if (!handle || !(handle in tentboxWeights)) {
        return
      }

      const tentboxWeight = tentboxWeights[handle]
      const isCompatible = !isNaN(tentboxWeight) && tentboxWeight <= roofLoadLimit

      const compatBlocks = {
        compatibleMobile: card.querySelector('.tentbox-card__compatibility.tentbox-card__compatibility--mobile.tentbox-card__compatibility--compatible'),
        incompatibleMobile: card.querySelector('.tentbox-card__compatibility.tentbox-card__compatibility--mobile.tentbox-card__compatibility--incompatible'),
      }

      // Helper to toggle visibility
      const setVisibility = (el, show) => {
        el.classList.toggle('tentbox-card__compatibility--concealed', !show)
      }

      Object.values(compatBlocks).forEach(el => setVisibility(el, false))

      // Add compatibility state classes to card
      card.classList.remove('show-compatible', 'show-incompatible')

      if (isCompatible) {
        const targetElement = compatBlocks.compatibleMobile
        setVisibility(targetElement, true)
        card.classList.add('show-compatible')
      } else {
        const targetElement = compatBlocks.incompatibleMobile
        setVisibility(targetElement, true)
        card.classList.add('show-incompatible')
      }
    })

    // Ensure we react to future resizes only once
    if (!this._compatResizeListenerAdded) {
      this._compatResizeListenerAdded = true
      window.addEventListener('resize', () => {
        if (this._lastTentboxesData && this._lastRoofLoadLimit !== undefined && this.resultsRendered) {
          this.updateCardCompatibility(this._lastTentboxesData, this._lastRoofLoadLimit)
        }
      })
    }
  }

  updateNoticeBox (roofLoadLimit, scenario) {
    if (!this.noticeMount) return
    this.noticeMount.innerHTML = ''

    const shortcodeValues = {
      roof_load_limit: roofLoadLimit,
      manufacturer_roof_load_limit: this.roofLoadMeta?.manufacturer ?? '',
    }

    let bodyHtml = ''
    if (scenario === 'incompatible') {
      bodyHtml = this.replaceShortcodes(
        this.translations?.notice_incompatible || '<p>This advice is based on real-world testing and the specific design of your vehicle’s roof.</p>',
        shortcodeValues,
      )
    } else if (scenario === 'overridden') {
      bodyHtml = this.replaceShortcodes(this.translations?.notice_overridden || '', shortcodeValues)
      const footnoteHtml = this.replaceShortcodes(this.translations?.notice_overridden_footnote || '', shortcodeValues)
      if (footnoteHtml) {
        bodyHtml += `<div class="vc-widget__notice-footnote mt-3">${footnoteHtml}</div>`
      }
    } else {
      bodyHtml = this.replaceShortcodes(
        this.translations?.notice_standard || '<p>Your vehicle can hold [[s]][[roof_load_limit]]kg[[/s]] on the roof whilst driving.</p>',
        shortcodeValues,
      )
    }

    const notice = document.createElement('div')
    notice.classList.add(
      'vc-widget__notice', 'flex', 'flex-col', 'md:flex-row', 'items-start', 'gap-3',
      'rounded-lg', 'bg-[#ecf8f1]', 'px-2', 'py-3', 'md:p-4', 'text-black',
    )
    notice.innerHTML = `
      <span class="vc-widget__notice-icon tb-icon w-6 h-6 shrink-0 ml-1 md:ml-0">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
        </svg>
      </span>
      <div class="vc-widget__notice-text flex-1 text-base">${bodyHtml}</div>`

    this.noticeMount.appendChild(notice)
  }

  createShineLoader () {
    const shineLoader = document.createElement('div')
    shineLoader.classList.add('skeleton-shine', 'absolute', 'inset-0', 'z-10', 'w-full', 'h-full', 'pointer-events-none')
    shineLoader.innerHTML = `<span class="sr-only">${this.replaceShortcodes(this.translations?.loading || 'Loading...')}</span>`
    return shineLoader
  }

  hasSmsStepBeenOffered () {
    return localStorage.getItem('vehicleCheckerSmsOffered') === '1'
  }

  markSmsStepOffered () {
    localStorage.setItem('vehicleCheckerSmsOffered', '1')
  }

  openEmailModal () {
    const template = document.getElementById('email-modal-template')
    if (!template) return this.showResultsDirectly()
    this._offerSmsStep = this.smsStepEnabled && !this.hasSmsStepBeenOffered()

    const modal = document.querySelector('reusable-modal')
    const modalBody = modal?.querySelector('.reusable-modal__body')
    const modalTitle = modal?.querySelector('.reusable-modal__title')

    if (!modalBody) return this.showResultsDirectly()

    if (modalTitle) modalTitle.textContent = ''

    const content = template.content.cloneNode(true)
    modalBody.innerHTML = ''
    modalBody.appendChild(content)

    this.setupEmailModalHandlers(modalBody)

    this._modalCloseHandler = () => {
      this.showResultsDirectly()
      window.removeEventListener('vehicle-checker:modal-closed', this._modalCloseHandler)
    }
    window.addEventListener('vehicle-checker:modal-closed', this._modalCloseHandler)

    window.dispatchEvent(new CustomEvent('open:modal', {
      detail: {
        width: '500px',
        padding: 'none',
        headerClassAdd: ['hidden'],
        onCloseEvent: 'vehicle-checker:modal-closed',
      },
    }))

    // reusable-modal autofocuses the first focusable element (the skip button, since it
    // precedes the email input in DOM order) 50ms after open:modal - refocus afterwards
    setTimeout(() => {
      modalBody.querySelector('#vehicle-checker-email')?.focus({ preventScroll: true })
    }, 60)
  }

  getGeoCountry () {
    try {
      const session = sessionStorage.getItem('tentboxSessionIpInfo')
      if (session) {
        const parsed = JSON.parse(session)
        const countryCode = parsed?.country_code || parsed?.country
        if (countryCode) return String(countryCode).toUpperCase()
      }
    } catch (error) {
      // ignore malformed cache
    }

    try {
      const cached = localStorage.getItem('tentboxIpInfo')
      if (cached) {
        const parsed = JSON.parse(cached)
        const countryCode = parsed?.data?.country_code || parsed?.data?.country
        if (countryCode) return String(countryCode).toUpperCase()
      }
    } catch (error) {
      // ignore malformed cache
    }

    return ''
  }

  setupTrackingConsent (modalBody) {
    this._trackingConsentBlocks = Array.from(modalBody.querySelectorAll('[data-tracking-consent]'))

    if (!this._trackingConsentBlocks.length || !this.trackingConsentCountries.length) return

    this.applyTrackingConsentVisibility()

    if (!this.trackingConsentActive) {
      this.pollGeoCountry()
    }
  }

  applyTrackingConsentVisibility () {
    if (!this._trackingConsentBlocks || !this._trackingConsentBlocks.length) return

    const detectedCountry = this.getGeoCountry()

    this.trackingConsentActive = this.trackingConsentCountries.includes(this.localeCountry) ||
      this.trackingConsentCountries.includes(detectedCountry)

    this._trackingConsentBlocks.forEach((block) => {
      block.classList.toggle('hidden', !this.trackingConsentActive)
    })
  }

  pollGeoCountry (attempt = 0) {
    const maxAttempts = 15

    if (attempt >= maxAttempts) return

    setTimeout(() => {
      this.applyTrackingConsentVisibility()

      if (!this.trackingConsentActive) {
        this.pollGeoCountry(attempt + 1)
      }
    }, 200)
  }

  setupEmailModalHandlers (modalBody) {
    const emailView = modalBody.querySelector('[data-modal-view="email"]')
    const smsView = modalBody.querySelector('[data-modal-view="sms"]')
    const emailForm = modalBody.querySelector('[data-modal-form="email"]')
    const smsForm = modalBody.querySelector('[data-modal-form="sms"]')

    this.setupTrackingConsent(modalBody)

    const phoneFields = modalBody.querySelector('[data-modal-phone-fields]')
    if (phoneFields) {
      phoneFields.outerHTML = this.renderModalPhoneField(phoneFields)
      this.setupModalCountrySelector(modalBody)
    }

    modalBody.querySelectorAll('.klaviyo-signup__input').forEach((input) => {
      input.addEventListener('input', () => this.clearModalError(input))
    })

    modalBody.querySelectorAll('[data-modal-skip]').forEach((btn) => {
      btn.addEventListener('click', () => {
        window.dispatchEvent(new CustomEvent('close:modal'))
      })
    })

    emailForm?.addEventListener('submit', async (e) => {
      e.preventDefault()
      const emailInput = emailForm.querySelector('input[name="email"]')
      const email = emailInput?.value.trim()
      this.clearModalError(emailInput)

      if (!email || !this.isValidEmail(email)) {
        this.showModalError(emailInput, 'Please enter a valid email address.')
        return
      }

      this._captureEmail = email

      this.applyTrackingConsentVisibility()
      const trackingConsentInput = emailForm.querySelector('input[name="open_tracking_consent"]')
      let openTrackingConsent
      if (trackingConsentInput && this.trackingConsentActive) {
        openTrackingConsent = trackingConsentInput.checked ? 'SUBSCRIBED' : 'UNSUBSCRIBED'
      }

      const submitBtn = emailForm.querySelector('.vehicle-checker-email-modal__submit')
      this.setModalButtonLoading(submitBtn, true)
      const result = await this.submitResults({ step: 'email', email, openTrackingConsent })
      this.setModalButtonLoading(submitBtn, false)

      if (!result.ok) {
        this.showModalError(emailInput, result.error || 'Something went wrong. Please try again.')
        return
      }

      if (emailView && smsView && this._offerSmsStep) {
        this.markSmsStepOffered()
        emailView.classList.add('hidden')
        smsView.classList.remove('hidden')
      } else {
        window.dispatchEvent(new CustomEvent('close:modal'))
      }
    })

    smsForm?.addEventListener('submit', async (e) => {
      e.preventDefault()
      const countryInput = smsForm.querySelector('input[name="country_code"]')
      const phoneInput = smsForm.querySelector('input[name="phone"]')
      this.clearModalError(phoneInput)

      const countryCode = countryInput?.value || ''
      const rawPhone = phoneInput?.value.trim() || ''
      const validation = this.validateModalPhone(countryCode, rawPhone)
      if (!validation.valid) {
        this.showModalError(phoneInput, validation.message)
        return
      }

      const submitBtn = smsForm.querySelector('.vehicle-checker-email-modal__submit')
      this.setModalButtonLoading(submitBtn, true)
      const result = await this.submitResults({ step: 'sms', email: this._captureEmail, phone: validation.e164 })
      this.setModalButtonLoading(submitBtn, false)

      if (!result.ok) {
        this.showModalError(phoneInput, result.error || 'Something went wrong. Please try again.')
        return
      }

      window.dispatchEvent(new CustomEvent('close:modal'))
    })
  }

  async submitResults ({ step, email, phone, openTrackingConsent }) {
    const payload = {
      step,
      email,
      list_id: this.klaviyoListId,
      store: this.store,
      make: this.selectedBrand || '',
      model: this.selectedSeries || '',
      weight: this.roofLoadLimit !== null ? Number(this.roofLoadLimit) : 0,
      market: this.market,
    }
    if (phone) payload.phone = phone
    if (openTrackingConsent) payload.open_tracking_consent = openTrackingConsent
    if (this.signupSource) payload.api_signup_source = this.signupSource

    const signupPage = typeof window !== 'undefined' ? window.location?.pathname : ''
    if (signupPage) payload.api_signup_page = signupPage

    try {
      const response = await fetch(`${this.apiBase}/results`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      let data = {}
      try { data = await response.json() } catch (parseError) { data = {} }
      if (!response.ok) {
        return { ok: false, error: data.error }
      }
      return { ok: true }
    } catch (error) {
      console.error('Vehicle Checker results submission failed:', error)
      return { ok: false }
    }
  }

  normaliseNationalNumber (countryCode, phone) {
    let digits = String(phone).replace(/\D/g, '')
    const cc = String(countryCode).replace(/\D/g, '')
    const rule = VC_PHONE_RULES[countryCode] || VC_DEFAULT_PHONE_RULE
    if (digits.startsWith('00')) digits = digits.slice(2)
    if (cc && digits.startsWith(cc) && digits.length > rule.max) {
      const stripped = digits.slice(cc.length)
      if (stripped.length >= rule.min && stripped.length <= rule.max) digits = stripped
    }
    if (digits.startsWith('0')) digits = digits.slice(1)
    return digits
  }

  validateModalPhone (countryCode, phone) {
    if (!countryCode) {
      return { valid: false, message: 'Please select a country code.' }
    }
    if (!phone) {
      return { valid: false, message: 'Please enter a valid phone number.' }
    }
    const national = this.normaliseNationalNumber(countryCode, phone)
    const rule = VC_PHONE_RULES[countryCode] || VC_DEFAULT_PHONE_RULE
    if (national.length < rule.min || national.length > rule.max) {
      return { valid: false, message: 'Please enter a valid phone number.' }
    }
    if (rule.mobilePrefixes && !rule.mobilePrefixes.some((prefix) => national.startsWith(prefix))) {
      return { valid: false, message: 'Please enter a valid mobile number that can receive SMS.' }
    }
    const e164 = `${countryCode}${national}`
    if (!/^\+[1-9]\d{6,14}$/.test(e164)) {
      return { valid: false, message: 'Please enter a valid phone number.' }
    }
    return { valid: true, e164 }
  }

  setModalButtonLoading (button, loading) {
    if (!button) return
    button.classList.toggle('vehicle-checker-email-modal__submit--loading', loading)
    button.disabled = loading
    button.setAttribute('aria-busy', loading ? 'true' : 'false')
  }

  showModalError (input, message) {
    if (!input) return
    input.classList.add('border-danger')
    const field = input.closest('.klaviyo-signup__field, .vehicle-checker-email-modal__field')
    const errorDiv = field?.querySelector('.klaviyo-signup__error, .vehicle-checker-email-modal__error')
    if (errorDiv) {
      const span = errorDiv.querySelector('span')
      if (span) span.textContent = message
      errorDiv.classList.remove('hidden')
    }
  }

  clearModalError (input) {
    if (!input) return
    input.classList.remove('border-danger')
    const field = input.closest('.klaviyo-signup__field, .vehicle-checker-email-modal__field')
    const errorDiv = field?.querySelector('.klaviyo-signup__error, .vehicle-checker-email-modal__error')
    if (errorDiv) errorDiv.classList.add('hidden')
  }

  renderModalPhoneField (container) {
    const phoneLabel = container.dataset.phoneLabel || 'Mobile number'
    const countryLabel = container.dataset.countryLabel || 'Country'
    const phonePlaceholder = container.dataset.phonePlaceholder || '7123456789'
    const defaultCountryCode = this.getDefaultCountryCode()
    const countryOptions = this.getCountryCodeOptions()
    const selectedCountry = countryOptions.find(c => c.code === defaultCountryCode) || countryOptions[0]
    const popoverId = 'vc-modal-country-popover'

    return `
      <div class="klaviyo-signup__field">
        <label for="vehicle-checker-phone" class="text-sm text-iron mb-1 block">
          <span>${phoneLabel}</span>
        </label>
        <div class="flex gap-0 group klaviyo-signup__phone-group w-full min-w-0">
          <div class="klaviyo-signup__country-selector relative shrink-0">
            <input type="hidden" name="country_code" value="${selectedCountry.code}" required />
            <button
              type="button"
              popovertarget="${popoverId}"
              class="klaviyo-signup__country-button h-11.5 rounded-l rounded-r-none border border-steel px-2.5 md:px-3 focus-visible:outline-hidden text-base text-black focus:ring-0 focus:border-black group-hover:border-black group-focus:border-black group-active:border-black flex items-center gap-1.5 bg-white"
              aria-label="${countryLabel}"
            >
              <span class="klaviyo-signup__country-flag w-5 h-5 flex items-center justify-center">${selectedCountry.flag}</span>
              <span class="klaviyo-signup__country-code shrink-0">${selectedCountry.code}</span>
              <span class="tb-icon w-3 h-3 shrink-0">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </span>
            </button>
            <div
              id="${popoverId}"
              popover
              class="klaviyo-signup__country-popover bg-white border border-steel rounded-lg shadow-lg p-0 m-0 max-h-75 overflow-y-auto"
            >
              ${countryOptions.map(country => `
                <button
                  type="button"
                  class="klaviyo-signup__country-option w-full flex items-center gap-2 px-3 py-2 hover:bg-oat transition-colors text-left"
                  data-country-code="${country.code}"
                >
                  <span class="w-5 h-5 flex items-center justify-center flex-shrink-0">${country.flag}</span>
                  <span class="text-base">${country.code} ${country.name}</span>
                </button>
              `).join('')}
            </div>
          </div>
          <input
            type="tel"
            name="phone"
            id="vehicle-checker-phone"
            placeholder="${phonePlaceholder}"
            aria-label="${phoneLabel}"
            class="klaviyo-signup__input h-11.5 flex-1 min-w-0 w-full rounded-r rounded-l-none border border-l-0 border-steel px-3.5 py-2.5 md:px-4.5 md:py-3 placeholder-battleship focus-visible:outline-hidden text-base text-black focus:ring-0 focus:border-black group-hover:border-black group-focus:border-black group-active:border-black"
            required
          />
        </div>
        <div class="klaviyo-signup__error text-danger text-sm mt-1 hidden"><span></span></div>
      </div>
    `
  }

  getCountryCodeOptions () {
    const flags = window.Tentbox?.storeSelector?.flags || {}

    return [
      { code: '+44', name: 'UK', flag: this.getCountryFlag('gb', flags) },
      { code: '+353', name: 'Ireland', flag: this.getCountryFlag('ie', flags) },
      { code: '+33', name: 'France', flag: this.getCountryFlag('fr', flags) },
      { code: '+49', name: 'Germany', flag: this.getCountryFlag('de', flags) },
      { code: '+31', name: 'Netherlands', flag: this.getCountryFlag('nl', flags) },
      { code: '+39', name: 'Italy', flag: this.getCountryFlag('it', flags) },
      { code: '+34', name: 'Spain', flag: this.getCountryFlag('es', flags) },
      { code: '+351', name: 'Portugal', flag: this.getCountryFlag('pt', flags) },
      { code: '+32', name: 'Belgium', flag: this.getCountryFlag('be', flags) },
      { code: '+41', name: 'Switzerland', flag: this.getCountryFlag('ch', flags) },
      { code: '+43', name: 'Austria', flag: this.getCountryFlag('at', flags) },
    ]
  }

  getCountryFlag (countryCode, flags) {
    const flagKey = `${countryCode}_circle`
    if (flags[flagKey]) {
      return flags[flagKey]
    }

    const flagEmojis = {
      nl: '🇳🇱',
      es: '🇪🇸',
      pt: '🇵🇹',
      be: '🇧🇪',
      ch: '🇨🇭',
      at: '🇦🇹',
    }

    if (flagEmojis[countryCode]) {
      return `<span class="text-lg">${flagEmojis[countryCode]}</span>`
    }

    return '<span class="text-lg">🏴</span>'
  }

  getDefaultCountryCode () {
    const shopPermanentDomain = window.Shopify?.shop || ''

    if (shopPermanentDomain === 'tentbox-6623.myshopify.com') {
      return '+44'
    }

    try {
      const geoData = localStorage.getItem('geolocationData')
      if (geoData) {
        const parsed = JSON.parse(geoData)
        const countryCode = parsed.country_code

        const countryToPhone = {
          GB: '+44',
          IE: '+353',
          FR: '+33',
          DE: '+49',
          NL: '+31',
          IT: '+39',
          ES: '+34',
          PT: '+351',
          BE: '+32',
          CH: '+41',
          AT: '+43',
        }

        return countryToPhone[countryCode] || '+44'
      }
    } catch (error) {
      console.log('Could not parse geolocation data:', error)
    }

    return '+44'
  }

  setupModalCountrySelector (modalBody) {
    modalBody.querySelectorAll('.klaviyo-signup__country-selector').forEach((selector) => {
      const button = selector.querySelector('.klaviyo-signup__country-button')
      const popover = selector.querySelector('[popover]')
      const countryOptions = selector.querySelectorAll('.klaviyo-signup__country-option')

      if (button && popover) {
        const supportsAnchorPositioning = CSS.supports('anchor-name', '--test')

        if (!supportsAnchorPositioning) {
          const positionPopover = () => {
            const buttonRect = button.getBoundingClientRect()
            popover.style.left = `${buttonRect.left}px`
            popover.style.top = `${buttonRect.bottom + 4}px`
          }

          const handleScroll = () => {
            if (popover.matches(':popover-open')) {
              positionPopover()
            }
          }

          button.addEventListener('click', () => {
            requestAnimationFrame(positionPopover)
          })

          popover.addEventListener('toggle', (e) => {
            if (e.newState === 'open') {
              window.addEventListener('scroll', handleScroll, true)
              window.addEventListener('resize', handleScroll)
            } else {
              window.removeEventListener('scroll', handleScroll, true)
              window.removeEventListener('resize', handleScroll)
            }
          })
        }
      }

      countryOptions.forEach((option) => {
        option.addEventListener('click', (e) => {
          const countryCode = e.currentTarget.dataset.countryCode
          this.selectModalCountryCode(countryCode, selector)

          if (popover) {
            popover.hidePopover()
          }
        })
      })
    })
  }

  selectModalCountryCode (code, selector) {
    const hiddenInput = selector.querySelector('input[name="country_code"]')
    const button = selector.querySelector('.klaviyo-signup__country-button')
    const codeSpan = button?.querySelector('.klaviyo-signup__country-code')
    const flagSpan = button?.querySelector('.klaviyo-signup__country-flag')

    if (hiddenInput) {
      hiddenInput.value = code
    }

    if (codeSpan) {
      codeSpan.textContent = code
    }

    if (flagSpan) {
      const countryOptions = this.getCountryCodeOptions()
      const selectedCountry = countryOptions.find(c => c.code === code)
      if (selectedCountry) {
        flagSpan.innerHTML = selectedCountry.flag
      }
    }
  }

  showResultsDirectly () {
    this.hideShowResultsButton()
    this.revealResults()
    this.scrollToResults()
  }

  revealResults () {
    if (this.resultContainer) {
      this.resultContainer.classList.remove('hidden')
    }
    if (this.tentboxesResultsContainer) {
      this.tentboxesResultsContainer.classList.remove('is-results-hidden')
    }
    this.showAllSections()

    const carousel = this.querySelector('carousel-component')
    if (carousel) {
      carousel.dispatchEvent(new CustomEvent('carousel:reinit'))
    }
  }

  hideResults () {
    if (this.resultContainer) {
      this.resultContainer.classList.add('hidden')
    }
    if (this.tentboxesResultsContainer) {
      this.tentboxesResultsContainer.classList.add('is-results-hidden')
    }
  }

  showShowResultsButton () {
    if (!this.showResultsContainer) {
      this.showResultsContainer = this.querySelector('.vc-widget__show-results')
    }
    if (this.showResultsContainer) {
      this.showResultsContainer.classList.remove('hidden')
    }
  }

  hideShowResultsButton () {
    if (!this.showResultsContainer) {
      this.showResultsContainer = this.querySelector('.vc-widget__show-results')
    }
    if (this.showResultsContainer) {
      this.showResultsContainer.classList.add('hidden')
    }
  }

  setInputsLoading (loading) {
    const inputs = [this.makeInput, this.modelInput, this.seriesInput, this.generationInput]
    inputs.forEach(input => {
      if (!input) return
      if (loading) {
        input.setAttribute('data-loading', '')
      } else {
        input.removeAttribute('data-loading')
      }
    })
  }

  handleShowResultsClick () {
    if (this._skipEmailModal || !this.emailModalEnabled) {
      this.showResultsDirectly()
    } else {
      this.openEmailModal()
    }
  }

  clearResults () {
    // Reset title to initial state
    if (this.tentboxesResultsTitle && this.initialTitleContent) {
      this.tentboxesResultsTitle.innerHTML = this.initialTitleContent
    }

    // Reset all cards to initial state (hide compatibility elements)
    const existingCards = this.tentboxesResultsContainer?.querySelectorAll('.tentbox-card')
    if (existingCards?.length) {
      existingCards.forEach(card => {
        // Hide all compatibility elements (both mobile and desktop)
        // Mobile compatibility elements are direct children of the card
        const mobileCompatBlocks = [
          '.tentbox-card__compatibility.tentbox-card__compatibility--mobile.tentbox-card__compatibility--compatible',
          '.tentbox-card__compatibility.tentbox-card__compatibility--mobile.tentbox-card__compatibility--incompatible',
        ]

        mobileCompatBlocks.forEach(selector => {
          const element = card.querySelector(selector)
          if (element) element.classList.add('hidden')
        })

        // Remove pointer-events styling and compatibility state classes from main card
        card.classList.remove('pointer-events-none', 'show-compatible', 'show-incompatible')

        // Reset specific card sections to initial state
        const contentHeader = card.querySelector('.tentbox-card__content--header')
        const contentFooter = card.querySelector('.tentbox-card__footer')
        const cardButton = card.querySelector('.tentbox-card__actions a')
        const rating = card.querySelector('.tentbox-card__rating')
        const productImage = card.querySelector('.tentbox-card__image img')

        if (rating) rating.classList.remove('opacity-50')
        if (productImage) productImage.classList.remove('opacity-50')
        if (contentHeader) contentHeader.classList.remove('opacity-50')
        if (contentFooter) contentFooter.classList.remove('opacity-50')
        if (cardButton) cardButton.classList.remove('hidden')
      })
    }

    if (this.noticeMount) {
      this.noticeMount.innerHTML = ''
    }
    if (this.checkAnotherContainer) {
      this.checkAnotherContainer.classList.add('hidden')
    }
    this.resetCompatibilityChips()

    // Reset container gap and padding to initial state
    if (this.tentboxesResultsContainer) {
      this.tentboxesResultsContainer.classList.remove('gap-18', 'pt-10')
      this.tentboxesResultsContainer.classList.add('gap-2.5')
    }

    // Keep results container visible but in initial state
    this.showElement(this.tentboxesResultsEl)
    this.hideResults()
    this.hideShowResultsButton()

    this.showElement(this.contactCTA)
    this.toggleChatWidget(true)
    this.hideAllSections()
    this.resultsRendered = false

    this._lastTentboxesData = null
    this._lastRoofLoadLimit = undefined
    this._lastRenderedSelectionKey = null
  }

  getLargeLoadingSpinner () {
    return `
      <div class="loading-overlay__spinner w-full h-full flex items-center justify-center py-24" role="status" aria-live="polite">
        <div class="h-12 w-12 text-black animate-spin">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 12a9 9 0 1 1-6.219-8.56"></path>
          </svg>
        </div>
        <span class="sr-only">Loading results...</span>
      </div>`
  }

  async restorePreviousSelection () {
    const sharedInfoRaw = localStorage.getItem('sharedVehicleInfo')
    if (sharedInfoRaw) {
      localStorage.removeItem('sharedVehicleInfo')
      try {
        const sharedInfo = JSON.parse(sharedInfoRaw)
        if (sharedInfo?.make) {
          await this.restoreFromUrlParams({
            make: sharedInfo.make,
            series: sharedInfo.model,
          })
          return
        }
      } catch (err) {
        console.error('Error parsing shared vehicle info:', err)
      }
    }
    const storedSelectionRaw = localStorage.getItem('vehicleSelection')
    if (storedSelectionRaw) {
      try {
        const selection = JSON.parse(storedSelectionRaw)
        if (selection?.brand?.name && selection?.series?.name && selection?.model?.name) {
          await this.restoreFromUrlParams({
            make: selection.brand.name,
            series: selection.series.name,
            model: selection.model.name,
            generation: selection.generation?.label === this.replaceShortcodes(this.translations?.all_generations || 'All Generations') ? null : selection.generation?.label,
            lastInput: 'generation',
          })
          if (this.isSelectionComplete() && this.roofLoadLimit !== null && selection.roofLoadLimit) {
            await this.renderTentboxesResult(selection.roofLoadLimit || this.roofLoadLimit)
          }
        } else {
          console.warn('Stored selection incomplete, ignoring:', selection)
          localStorage.removeItem('vehicleSelection')
        }
      } catch (err) {
        console.error('Error parsing stored vehicle selection:', err)
        localStorage.removeItem('vehicleSelection')
      }
    }
  }

  handleSuggestionsKeydown (e, input, container, selectCallback) {
    const items = Array.from(container.querySelectorAll('li[data-value]'))
    if (items.length === 0) return
    const currentFocusedElement = document.activeElement
    let currentIndex = -1
    if (container.contains(currentFocusedElement) && currentFocusedElement.tagName === 'LI') {
      currentIndex = items.indexOf(currentFocusedElement)
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        const nextIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0
        items[nextIndex]?.focus()
        break
      case 'ArrowUp':
        e.preventDefault()
        if (currentIndex > 0) {
          items[currentIndex - 1]?.focus()
        } else if (currentIndex === 0) {
          input.focus()
        } else {
          items[items.length - 1]?.focus()
        }
        break
      case 'Enter':
      case ' ':
        if (currentIndex !== -1) {
          e.preventDefault()
          selectCallback(items[currentIndex])
          this.hideSuggestions(container)
        }
        break
      case 'Escape':
        e.preventDefault()
        this.hideSuggestions(container)
        input.focus()
        break
      case 'Tab':
        this.hideSuggestions(container)
        break
    }
  }

  focusNextInput (currentInput) {
    const nextInputType = this.determineNextInput(currentInput.id.split('-')[0])
    this.focusInputElement(nextInputType)
  }

  dismissKeyboard () {
    this.blurActiveElement()
  }

  set loading (value) {
    if (value) {
      this.setAttribute('data-loading', '')
    } else {
      this.removeAttribute('data-loading')
    }
  }

  get loading () {
    return this.hasAttribute('data-loading')
  }

  replaceShortcodes (text, additionalValues = {}) {
    if (!text) return ''
    const shortcodeValues = {
      selected_make: this.displayLabel(this.selectedBrand) || '',
      selected_model: this.displayLabel(this.selectedSeries) || '',
      selected_series: this.displayLabel(this.selectedModel) || '',
      selected_generation: this.displayLabel(this.selectedGeneration) || '',
      roof_load_limit: this.roofLoadLimit !== null ? this.roofLoadLimit : 'N/A',
      manufacturer_roof_load_limit: this.roofLoadMeta?.manufacturer ?? 'N/A',
      ...additionalValues,
    }
    let result = text
    result = result.replace(/\[\[s\]\]/g, '<strong>').replace(/\[\[\/s\]\]/g, '</strong>')
    if (this.userGuidelinesUrl) {
      result = result
        .replace(/\[\[guidelines\]\]/g, `<a href="${this.userGuidelinesUrl}" class="underline underline-offset-2">`)
        .replace(/\[\[\/guidelines\]\]/g, '</a>')
    } else {
      result = result.replace(/\[\[guidelines\]\]/g, '').replace(/\[\[\/guidelines\]\]/g, '')
    }
    for (const [key, value] of Object.entries(shortcodeValues)) {
      const regex = new RegExp(`\\[\\[${key}\\]\\]`, 'g')
      result = result.replace(regex, value)
    }
    return result
  }

  showMaintenanceMessage (message = '') {
    if (this.maintenanceMode) return
    this.maintenanceMode = true
    console.warn('Vehicle Checker entering maintenance mode.');
    [this.makeInput, this.modelInput, this.seriesInput, this.generationInput].forEach(input => {
      if (input) input.disabled = true
    });
    [this.makeResetBtn, this.modelResetBtn, this.seriesResetBtn, this.generationResetBtn].forEach(btn => this.hideElement(btn));
    [this.makeSuggestions, this.modelSuggestions, this.seriesSuggestions, this.generationSuggestions].forEach(s => this.hideSuggestions(s))
    this.clearResults()
    this.buttonManager.hideAllButtons()
    this.querySelector('.vehicle-checker-maintenance-alert')?.remove()
    const maintenanceAlert = document.createElement('div')
    maintenanceAlert.classList.add(
      'vehicle-checker-maintenance-alert', 'flex', 'gap-3', 'items-start', 'rounded-lg', 'p-4', 'border',
      'bg-red-100', 'border-red-500', 'text-red-900',
      'max-w-screen-xl', 'mx-auto', 'mb-8',
    )
    maintenanceAlert.setAttribute('role', 'alert')
    maintenanceAlert.innerHTML = `
      <div class="w-5 h-5 mt-1 flex-shrink-0">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path fill-rule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16zM8.28 7.22a.75.75 0 0 0-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 1 0 1.06 1.06L10 11.06l1.72 1.72a.75.75 0 1 0 1.06-1.06L11.06 10l1.72-1.72a.75.75 0 0 0-1.06-1.06L10 8.94 8.28 7.22z" clip-rule="evenodd"></path>
        </svg>
      </div>
      <div class="flex-1">
        ${message || 'Our apologies, this tool is currently unavailable due to a connection issue or maintenance. Please try again shortly.'}
      </div>`
    this.querySelector('.vc-widget')?.insertAdjacentElement('beforebegin', maintenanceAlert)
  }

  toggleChatWidget (show) {
    const chat = document.querySelector('#shopify-section-chat')
    if (chat) {
      if (show) {
        chat.classList.remove('hidden')
      } else {
        chat.classList.add('hidden')
      }
    }
  }

  getSessionId () {
    let sessionId = sessionStorage.getItem('tentboxSessionId')
    if (!sessionId) {
      sessionId = Math.random().toString(36).substring(2) + Date.now().toString(36)
      sessionStorage.setItem('tentboxSessionId', sessionId)
    }
    return sessionId
  }

  async logSearch () {
    try {
      const ipInfo = JSON.parse(localStorage.getItem('tentboxIpInfo') || '{"data": {}}')
      const ip = ipInfo?.data?.ip || null
      const sessionId = this.getSessionId()
      const roofLoadLimit = this.roofLoadLimit || null
      const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : null

      const response = await this.apiFetch('/log-result', {
        method: 'POST',
        body: JSON.stringify({
          tool: 'vehicleChecker',
          make: this.selectedBrand,
          model: this.selectedModel,
          series: this.selectedSeries,
          generation: this.selectedGeneration,
          ip,
          session_id: sessionId,
          roof_load_limit: roofLoadLimit,
          user_agent: userAgent,
        }),
      })
      if (!response?.success) {
        console.warn('Failed to log search to proxy server')
      }
    } catch (err) {
      console.error('Error logging search:', err)
    }
  }

  buildCompatibilityAnalyticsData (validTentboxes, compatibleTentboxes, roofLoadLimit) {
    const compatible = compatibleTentboxes.map(tentbox => ({
      handle: tentbox.handle || '',
      title: tentbox.title || tentbox.handle || '',
      weight: Number(tentbox.weight),
    }))

    const compatibleHandles = new Set(compatible.map(tentbox => tentbox.handle))
    const incompatible = validTentboxes
      .filter(tentbox => !compatibleHandles.has(tentbox.handle))
      .map(tentbox => ({
        handle: tentbox.handle || '',
        title: tentbox.title || tentbox.handle || '',
        weight: Number(tentbox.weight),
      }))

    const heaviestCompatible = compatible.length
      ? compatible.reduce((heaviest, tentbox) => (tentbox.weight > heaviest.weight ? tentbox : heaviest), compatible[0])
      : null

    const lightestCompatible = compatible.length
      ? compatible.reduce((lightest, tentbox) => (tentbox.weight < lightest.weight ? tentbox : lightest), compatible[0])
      : null

    const formatList = (items, key) => items.map(item => item[key]).join(', ')

    return {
      vechicle_checker_roof_load_limit: roofLoadLimit,
      vechicle_checker_compatible_count: compatible.length,
      vechicle_checker_incompatible_count: incompatible.length,
      vechicle_checker_total_tentboxes: validTentboxes.length,
      vechicle_checker_has_compatible: compatible.length > 0,
      vechicle_checker_compatible_handles: formatList(compatible, 'handle'),
      vechicle_checker_compatible_titles: formatList(compatible, 'title'),
      vechicle_checker_compatible_weights: formatList(compatible, 'weight'),
      vechicle_checker_incompatible_handles: formatList(incompatible, 'handle'),
      vechicle_checker_incompatible_titles: formatList(incompatible, 'title'),
      vechicle_checker_incompatible_weights: formatList(incompatible, 'weight'),
      vechicle_checker_heaviest_compatible_handle: heaviestCompatible?.handle || '',
      vechicle_checker_heaviest_compatible_title: heaviestCompatible?.title || '',
      vechicle_checker_heaviest_compatible_weight: heaviestCompatible?.weight ?? '',
      vechicle_checker_lightest_compatible_handle: lightestCompatible?.handle || '',
      vechicle_checker_lightest_compatible_title: lightestCompatible?.title || '',
      vechicle_checker_lightest_compatible_weight: lightestCompatible?.weight ?? '',
    }
  }

  trackAnalyticsEvent (eventName, eventLabel, extraEventModel = null) {
    try {
      if (typeof window !== 'undefined' && window.dataLayer) {
        window.dataLayer.push({
          event: eventName,
          eventModel: {
            vechicle_checker_make: this.selectedBrand || '',
            vechicle_checker_model: this.selectedSeries || '',
            vechicle_checker_series: this.selectedModel || '',
            vechicle_checker_generation: this.selectedGeneration || '',
            ...(extraEventModel || {}),
          },
        })
      }
    } catch (error) {
      console.warn('Error tracking analytics event:', eventName, error)
    }
  }

  isValidEmail (email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  calculateHeaderHeight () {
    let totalHeight = 0
    const isMobile = window.innerWidth < 768

    // Elements to consider for height calculation
    const headerElements = [
      '.site-header',
    ]

    // Add guide-steps-nav only on mobile
    if (isMobile) {
      headerElements.push('.guide-steps-nav')
    }

    headerElements.forEach(elementSelector => {
      const element = document.querySelector(elementSelector)
      if (element) {
        const rect = element.getBoundingClientRect()
        totalHeight += rect.height
      }
    })

    return totalHeight
  }

  findItemWithFlexibleMatching (items, searchTerm, property = 'name') {
    if (!items || !searchTerm) return null
    let match = items.find(item =>
      item[property].toLowerCase() === searchTerm.toLowerCase(),
    )
    if (match) return match
    const normalizedSearch = this.normalizeString(searchTerm)
    match = items.find(item =>
      this.normalizeString(item[property]) === normalizedSearch,
    )
    if (match) return match
    match = items.find(item =>
      item[property].toLowerCase().includes(searchTerm.toLowerCase()),
    )
    if (match) return match
    match = items.find(item =>
      searchTerm.toLowerCase().includes(item[property].toLowerCase()),
    )
    return match
  }

  isStringMatch (str1, str2) {
    if (!str1 || !str2) return false
    if (str1.toLowerCase() === str2.toLowerCase()) return true
    if (this.normalizeString(str1) === this.normalizeString(str2)) return true
    if (str1.replace(/\+/g, ' ').toLowerCase() === str2.toLowerCase()) return true
    if (str1.toLowerCase() === str2.replace(/\+/g, ' ').toLowerCase()) return true
    return false
  }

  highlightMatchingText (text, query) {
    if (!query || !text) return text

    const normalizedQuery = this.normalizeString(query)
    const normalizedText = this.normalizeString(text)

    // Try direct case-insensitive match first
    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const directRegex = new RegExp(`(${escapedQuery})`, 'gi')

    if (directRegex.test(text)) {
      return text.replace(directRegex, '<strong>$1</strong>')
    }

    // If no direct match, try diacritic-aware highlighting
    if (normalizedQuery && normalizedText.includes(normalizedQuery)) {
      const textArray = text.split('')
      const normalizedArray = normalizedText.split('')
      const queryArray = normalizedQuery.split('')

      // Find the start position of the match in normalized text
      const matchStart = normalizedText.indexOf(normalizedQuery)
      if (matchStart !== -1) {
        // Highlight the corresponding characters in the original text
        for (let i = 0; i < queryArray.length; i++) {
          const textIndex = matchStart + i
          if (textIndex < textArray.length) {
            textArray[textIndex] = `<strong>${textArray[textIndex]}</strong>`
          }
        }
        return textArray.join('')
      }
    }

    return text
  }
}
if (!customElements.get('vehicle-checker')) {
  customElements.define('vehicle-checker', VehicleChecker)
}
